import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatINR } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LineChart, Line, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import { Download, ScrollText, FileText, Play, Eye, LayoutDashboard, FolderTree, Package, Layers, CalendarDays, Sparkles, SlidersHorizontal, Scissors, ShoppingBag, Ticket, RotateCcw, Boxes, Building2, Quote, Image as ImageIcon, Wand2, Users, Contact, Mail, Activity, Bell, BarChart3, LogOut, RefreshCw, Film, MapPin } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";

const NAV = [
  { label: "Overview", items: [{ key: "overview", label: "Overview", icon: LayoutDashboard }] },
  { label: "Catalog", items: [
    { key: "product_categories", label: "Categories", icon: FolderTree },
    { key: "products", label: "Products", icon: Package },
    { key: "fabrics", label: "Fabrics", icon: Layers },
    { key: "seasonal_tiles", label: "Seasonal Spice", icon: Sparkles },
  ] },
  { label: "Bespoke", items: [
    { key: "bespoke_options", label: "Bespoke Options", icon: SlidersHorizontal },
    { key: "bespoke_categories", label: "Bespoke Categories", icon: Scissors },
  ] },
  { label: "Sales", items: [
    { key: "orders", label: "Orders", icon: ShoppingBag },
    { key: "coupons", label: "Coupons", icon: Ticket },
    { key: "returns", label: "Returns", icon: RotateCcw },
    { key: "bundles", label: "Bundles", icon: Boxes },
    { key: "corporate", label: "Corporate Gifting", icon: Building2 },
  ] },
  { label: "Content", items: [
    { key: "home_media", label: "Home Videos", icon: Film },
    { key: "testimonials", label: "Testimonials", icon: Quote },
    { key: "reels", label: "Reels", icon: Play },
    { key: "lookbook", label: "Lookbook", icon: ImageIcon },
    { key: "faqs", label: "FAQ", icon: FileText },
    { key: "recommendations", label: "Recommendations", icon: Wand2 },
  ] },
  { label: "People", items: [
    { key: "customers", label: "Customers", icon: Contact },
    { key: "users", label: "Users", icon: Users },
    { key: "crm", label: "CRM", icon: Contact },
    { key: "contacts", label: "Contacts", icon: Mail },
  ] },
  { label: "Operations", items: [
    { key: "locations", label: "Locations", icon: MapPin },
    { key: "scheduling", label: "Scheduling", icon: CalendarDays },
    { key: "lifecycle", label: "Lifecycle", icon: Activity },
    { key: "notifications", label: "Notifications", icon: Bell },
  ] },
  { label: "Insights", items: [
    { key: "statistics", label: "Statistics", icon: BarChart3 },
    { key: "reports", label: "Reports", icon: BarChart3 },
    { key: "audit", label: "Audit Log", icon: ScrollText },
  ] },
];
const ALL_ITEMS = NAV.flatMap((s) => s.items);

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState({ orders: [], bespoke: [] });
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [detailOrderId, setDetailOrderId] = useState(null);

  const refresh = async () => {
    const isStaff = user && ["super_admin", "major_admin"].includes(user.role);
    if (isStaff) {
      const { data } = await api.get("/admin/stats"); setStats(data);
    }
    const os = await api.get("/admin/orders"); setOrders(os.data);
    const us = await api.get("/admin/users"); setUsers(us.data);
    try { const cs = await api.get("/contact"); setContacts(cs.data); } catch {}
  };

  useEffect(() => { if (user && ["super_admin", "major_admin", "admin"].includes(user.role)) refresh(); }, [user]);

  if (loading) return <div className="sq-container py-20 sq-eyebrow">Loading control panel…</div>;
  if (!user) return <div className="sq-container py-20"><Link to="/auth" className="sq-btn-primary">Sign in as admin</Link></div>;
  if (!["super_admin", "major_admin", "admin"].includes(user.role)) return <div className="sq-container py-20">Access denied. <Link to="/" className="underline">Home</Link></div>;

  const downloadCSV = (rows, name) => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${name}.csv`; a.click();
  };

  const roleLabel = user.role.replace(/_/g, " ");

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="admin-dashboard">
      <Tabs defaultValue="overview" orientation="vertical" className="flex min-h-screen items-start">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-black/10 bg-white sticky top-0 h-screen">
          <div className="px-6 py-6 border-b border-black/10">
            <div className="sq-eyebrow text-squire-jeans">Squire &amp; Squire</div>
            <div className="font-display text-2xl mt-1 leading-none">Atelier Console</div>
            <Link to="/" className="inline-block mt-2 text-[11px] text-[#9CA3AF] hover:text-black transition-colors">← View storefront</Link>
          </div>
          <TabsList className="flex-1 flex flex-col items-stretch h-auto bg-transparent p-3 gap-0.5 overflow-y-auto justify-start rounded-none sq-scroll">
            {NAV.map((section) => (
              <div key={section.label}>
                <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF]">{section.label}</div>
                {section.items.map((it) => (
                  <TabsTrigger key={it.key} value={it.key} data-testid={`admin-tab-${it.key}`}
                    className="w-full justify-start gap-3 px-3 py-2 rounded-md text-sm font-normal text-[#4B5563] hover:bg-black/[0.04] data-[state=active]:bg-squire-jeans data-[state=active]:text-white data-[state=active]:shadow-none">
                    <it.icon size={16} className="shrink-0" /> {it.label}
                  </TabsTrigger>
                ))}
              </div>
            ))}
          </TabsList>
          <div className="px-3 py-3 border-t border-black/10">
            <button data-testid="admin-signout" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#4B5563] hover:bg-black/[0.04] rounded-md transition-colors"><LogOut size={16} /> Sign out</button>
          </div>
        </aside>

        {/* Content column */}
        <div className="flex-1 min-w-0">
          <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-black/10 px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="sq-eyebrow text-squire-jeans">Atelier Control</div>
              <h1 className="font-display text-2xl leading-none mt-1 capitalize">{roleLabel} Console</h1>
            </div>
            <div className="flex items-center gap-4">
              <button data-testid="admin-refresh" onClick={refresh} className="inline-flex items-center gap-1.5 border border-black/15 hover:border-black px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] transition-colors"><RefreshCw size={13} /> Refresh</button>
              <div className="text-right hidden sm:block">
                <div className="text-sm font-medium leading-none">{user.email}</div>
                <div className="text-[11px] text-[#9CA3AF] mt-1">{user.id}</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-squire-jeans text-white grid place-items-center font-display text-sm shrink-0">{(user.name || user.email || "A").slice(0, 1).toUpperCase()}</div>
            </div>
          </header>

          {/* Mobile / tablet nav */}
          <div className="lg:hidden border-b border-black/10 bg-white overflow-x-auto sq-scroll">
            <TabsList className="flex h-auto bg-transparent p-2 gap-1 w-max rounded-none justify-start">
              {ALL_ITEMS.map((it) => (
                <TabsTrigger key={it.key} value={it.key} data-testid={`admin-tab-m-${it.key}`}
                  className="whitespace-nowrap gap-1.5 px-3 py-1.5 text-xs rounded-md text-[#4B5563] data-[state=active]:bg-squire-jeans data-[state=active]:text-white data-[state=active]:shadow-none">
                  <it.icon size={13} /> {it.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="px-6 lg:px-10 py-8 max-w-[1400px]">

        {stats && (
          <TabsContent value="overview" className="mt-6 space-y-6" data-testid="admin-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Today's Revenue" value={formatINR(stats.today.revenue)} sub={`${stats.today.orders} orders`} />
              <Stat label="Week" value={formatINR(stats.week.revenue)} sub={`${stats.week.orders} orders`} />
              <Stat label="Month" value={formatINR(stats.month.revenue)} sub={`${stats.month.orders} orders`} />
              <Stat label="Total Business" value={formatINR(stats.total_business)} sub={`${stats.totals.orders + stats.totals.bespoke} orders`} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-black/10 rounded-lg p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="flex justify-between items-center mb-3">
                  <div className="sq-eyebrow">Revenue · last 14 days</div>
                  <button data-testid="download-revenue" onClick={() => downloadCSV(stats.daily_revenue, "revenue")} className="sq-btn-outline !py-1 !px-3 !text-[10px]"><Download size={12} /> CSV</button>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={stats.daily_revenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="revenue" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white border border-black/10 rounded-lg p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="sq-eyebrow mb-3">Orders by hour</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={stats.hourly_orders}>
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#0A0A0A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-black/10 rounded-lg p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="sq-eyebrow mb-3">Trending products</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {stats.trending.length === 0 && <div className="text-sm text-[#4B5563]">No trends yet — once orders flow in we'll show top movers.</div>}
                {stats.trending.map((p) => (
                  <div key={p.id} className="border border-black/10 p-3">
                    <img src={p.images?.[0]} alt={p.name} className="w-full aspect-[3/4] object-cover" />
                    <div className="font-display text-sm mt-2">{p.name}</div>
                    <div className="sq-eyebrow">{p.qty_sold} sold</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        )}
        {!stats && (
          <TabsContent value="overview" className="mt-6" data-testid="admin-overview">
            <div className="border border-black/10 bg-white rounded-lg p-10 text-center">
              <div className="w-12 h-12 rounded-full bg-squire-jeans/10 text-squire-jeans grid place-items-center mx-auto"><LayoutDashboard size={22} /></div>
              <h3 className="font-display text-xl mt-4">Welcome back</h3>
              <p className="text-sm text-[#4B5563] mt-1 max-w-md mx-auto">Revenue and analytics are available to senior admins. Use the menu to manage catalog, bespoke, orders, and content.</p>
            </div>
          </TabsContent>
        )}

        <TabsContent value="bespoke_options" data-testid="admin-bespoke-options">
          <PricingAdmin />
          <BespokeGuideAdmin />
          <ConfigOptionsAdmin />
          <GarmentSectionsAdmin />
          <MeasureFieldsAdmin />
        </TabsContent>

        <TabsContent value="locations" data-testid="admin-locations">
          <LocationsAdmin />
        </TabsContent>

        <TabsContent value="faqs" data-testid="admin-faqs">
          <FaqAdmin />
        </TabsContent>

        <TabsContent value="statistics" data-testid="admin-statistics">
          <StatisticsAdmin />
        </TabsContent>

        <TabsContent value="bespoke_categories" data-testid="admin-bespoke-categories">
          <BespokeCategoriesAdmin />
        </TabsContent>

        <TabsContent value="product_categories" data-testid="admin-product-categories">
          <ProductCategoriesAdmin />
        </TabsContent>

        <TabsContent value="products" data-testid="admin-products">
          <ProductsAdmin />
        </TabsContent>

        <TabsContent value="fabrics" data-testid="admin-fabrics">
          <FabricsAdmin />
        </TabsContent>

        <TabsContent value="home_media" data-testid="admin-home-media" className="mt-6">
          <HomeVideosAdmin />
        </TabsContent>

        <TabsContent value="testimonials" data-testid="admin-testimonials">
          <TestimonialsAdmin />
        </TabsContent>

        <TabsContent value="reels" data-testid="admin-reels">
          <ReelsAdmin />
        </TabsContent>

        <TabsContent value="seasonal_tiles" data-testid="admin-seasonal-tiles">
          <SeasonalTilesAdmin />
        </TabsContent>

        <TabsContent value="orders" data-testid="admin-orders" className="mt-6 space-y-3">
          <OrderSegmentsAdmin />
          <div className="border-t border-black/10 pt-6 mt-8">
          <div className="flex items-center justify-between mb-3"><div className="sq-eyebrow">All orders (quick list)</div><button data-testid="download-orders" onClick={() => downloadCSV(orders.orders, "orders")} className="sq-btn-outline !py-1 !px-3 !text-[10px]"><Download size={12} /> CSV</button></div>
          <table className="w-full text-sm">
            <thead><tr className="text-left sq-eyebrow"><th>ID</th><th>User</th><th>Total</th><th>Status</th><th>Date</th><th>Details</th></tr></thead>
            <tbody>
              {orders.orders.map((o) => (
                <tr key={o.id} className="border-t border-black/10">
                  <td>{o.id}</td><td>{o.user_id}</td><td>{formatINR(o.total)}</td><td><StatusBadge status={o.status} /></td><td>{o.created_at?.slice(0, 10)}</td>
                  <td><button data-testid={`order-view-${o.id}`} onClick={() => setDetailOrderId(o.id)} className="inline-flex items-center gap-1 text-xs hover:text-squire-jeans" title="View details"><Eye size={15} /> View</button></td>
                </tr>
              ))}
              {orders.bespoke.map((o) => (
                <tr key={o.id} className="border-t border-black/10 bg-squire-jeans/5">
                  <td>{o.id}</td><td>{o.user_id}</td><td>{formatINR(o.total)}</td><td><StatusBadge status={o.stage} /></td><td>{o.created_at?.slice(0, 10)}</td>
                  <td><button data-testid={`order-view-${o.id}`} onClick={() => setDetailOrderId(o.id)} className="inline-flex items-center gap-1 text-xs hover:text-squire-jeans" title="View details"><Eye size={15} /> View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <OrderDetailModal orderId={detailOrderId} onClose={() => setDetailOrderId(null)} />
          </div>
        </TabsContent>

        <TabsContent value="customers" data-testid="admin-customers" className="mt-6">
          <CustomersAdmin />
        </TabsContent>

        <TabsContent value="users" data-testid="admin-users" className="mt-6">
          <UsersAdmin users={users} onChange={refresh} />
        </TabsContent>

        <TabsContent value="coupons" data-testid="admin-coupons" className="mt-6">
          <CouponAdmin />
        </TabsContent>

        <TabsContent value="scheduling" data-testid="admin-scheduling" className="mt-6">
          <ScheduleAdmin />
        </TabsContent>

        <TabsContent value="returns" data-testid="admin-returns" className="mt-6">
          <ReturnsAdmin />
        </TabsContent>

        <TabsContent value="bundles" data-testid="admin-bundles" className="mt-6">
          <BundlesAdmin />
        </TabsContent>

        <TabsContent value="lookbook" data-testid="admin-lookbook" className="mt-6">
          <LookbookTilesAdmin />
          <LookbookAdmin />
        </TabsContent>

        <TabsContent value="crm" data-testid="admin-crm" className="mt-6">
          <CRMAdmin />
        </TabsContent>

        <TabsContent value="audit" data-testid="admin-audit" className="mt-6">
          <AuditAdmin />
        </TabsContent>

        <TabsContent value="reports" data-testid="admin-reports" className="mt-6">
          <ReportsAdmin />
        </TabsContent>

        <TabsContent value="lifecycle" data-testid="admin-lifecycle" className="mt-6">
          <LifecycleAdmin />
        </TabsContent>

        <TabsContent value="corporate" data-testid="admin-corporate" className="mt-6">
          <CorporateAdmin />
        </TabsContent>

        <TabsContent value="notifications" data-testid="admin-notifications" className="mt-6">
          <NotificationsAdmin />
        </TabsContent>

        <TabsContent value="recommendations" data-testid="admin-recommendations" className="mt-6">
          <RecoAdmin />
        </TabsContent>

        <TabsContent value="contacts" data-testid="admin-contacts" className="mt-6 space-y-3">
          {contacts.map((c) => (
            <div key={c.id} className="border border-black/10 p-4">
              <div className="flex justify-between"><div><strong>{c.name}</strong> · <span className="text-[#4B5563]">{c.email} · {c.phone}</span></div><div className="sq-eyebrow">{c.form_type} · {c.id}</div></div>
              <div className="text-sm mt-2"><strong>{c.subject}</strong> — {c.message}</div>
            </div>
          ))}
          {!contacts.length && <div className="text-[#4B5563] text-sm">No enquiries yet.</div>}
        </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-black/10 rounded-lg p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="sq-eyebrow text-[#9CA3AF]">{label}</div>
      <div className="font-display text-3xl mt-2">{value}</div>
      <div className="sq-eyebrow text-squire-jeans mt-1">{sub}</div>
    </div>
  );
}

const USER_ROLES = ["user", "admin", "master_manager", "packaging_manager", "major_admin", "super_admin"];

function CustomerDetailModal({ customerId, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!customerId) { setD(null); return; }
    setLoading(true);
    api.get(`/admin/customers/${customerId}`).then(({ data }) => setD(data)).catch(() => setD({ error: true })).finally(() => setLoading(false));
  }, [customerId]);

  const c = d?.customer || {};
  const s = d?.summary || {};
  return (
    <Dialog open={!!customerId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[86vh] overflow-y-auto" data-testid="customer-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{c.name || customerId}</DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm text-[#4B5563] py-8">Loading…</div>}
        {d?.error && <div className="text-sm text-[#4B5563] py-8">Could not load this customer.</div>}
        {d && !d.error && (
          <div className="mt-2 space-y-6">
            {/* Snapshot */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Orders", s.order_count], ["Total Spent", formatINR(s.total_spent)], ["Balance Due", formatINR(s.balance_due)], ["Loyalty", `${s.loyalty_points || 0} pts`]].map(([l, v]) => (
                <div key={l} className="bg-[#FAFAF8] border border-black/10 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">{l}</div>
                  <div className="font-display text-xl mt-1">{v}</div>
                </div>
              ))}
            </div>

            <Section title="Contact">
              <Row label="Customer ID" value={c.id} />
              <Row label="Name" value={c.name} />
              <Row label="Email" value={c.email} />
              <Row label="Phone" value={c.phone} />
              <Row label="Verified" value={c.verified ? "Yes" : "No"} />
              <Row label="Joined" value={(s.joined || "").slice(0, 10)} />
            </Section>

            <Section title={`Addresses (${d.addresses.length})`}>
              {d.addresses.length === 0 ? <div className="text-sm text-[#4B5563]">No addresses on file.</div> : (
                <div className="space-y-2">
                  {d.addresses.map((a, i) => (
                    <div key={i} className="text-sm border border-black/10 rounded-md p-3">
                      <div className="font-medium">{a.label || "Address"}</div>
                      {a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />
                      {a.city}{a.state ? `, ${a.state}` : ""}{a.pincode ? ` — ${a.pincode}` : ""}{a.phone ? ` · ${a.phone}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title={`Bespoke Orders (${d.bespoke.length})`}>
              {d.bespoke.length === 0 ? <div className="text-sm text-[#4B5563]">None.</div> : (
                <div className="space-y-2">
                  {d.bespoke.map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-3 border border-squire-jeans/30 bg-squire-jeans/[0.03] rounded-md p-3 text-sm">
                      <div>
                        <div className="font-medium">{o.id} · {o.garment_type}</div>
                        <div className="text-xs text-[#4B5563]">{(o.created_at || "").slice(0, 10)} · Advance {formatINR(o.advance_paid)} · Balance {formatINR(o.balance_due)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={o.stage} />
                        <span className="font-semibold">{formatINR(o.total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title={`Orders (${d.orders.length})`}>
              {d.orders.length === 0 ? <div className="text-sm text-[#4B5563]">None.</div> : (
                <div className="space-y-2">
                  {d.orders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-3 border border-black/10 rounded-md p-3 text-sm">
                      <div>
                        <div className="font-medium">{o.id}</div>
                        <div className="text-xs text-[#4B5563]">{(o.created_at || "").slice(0, 10)} · {(o.items || []).length} items</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={o.status} />
                        <span className="font-semibold">{formatINR(o.total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {d.measurements.length > 0 && (
              <Section title={`Measurements (${d.measurements.length})`}>
                <div className="space-y-2">
                  {d.measurements.map((m, i) => (
                    <div key={i} className="border border-black/10 rounded-md p-3 text-sm">
                      <div className="font-medium mb-1">{m.profile_name || "Profile"}</div>
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-xs">
                        {Object.entries(m).filter(([k, v]) => v && !["user_id", "id", "profile_name", "updated_at"].includes(k)).map(([k, v]) => (
                          <div key={k}><span className="text-[#9CA3AF] capitalize">{k.replace(/_/g, " ")}</span> {v}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {d.returns.length > 0 && (
              <Section title={`Returns & Alterations (${d.returns.length})`}>
                <div className="space-y-2">
                  {d.returns.map((r) => (
                    <div key={r.id} className="flex items-center justify-between border border-black/10 rounded-md p-3 text-sm">
                      <div><div className="font-medium">{r.id} · {(r.type || "").toUpperCase()}</div><div className="text-xs text-[#4B5563]">{r.order_id} · {r.reason}</div></div>
                      <StatusBadge status={r.status} />
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CustomersAdmin() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    api.get("/admin/customers").then(({ data }) => setCustomers(data || [])).catch(() => setCustomers([])).finally(() => setLoading(false));
  }, []);

  const shown = customers.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [c.name, c.email, c.phone, c.id].some((v) => String(v || "").toLowerCase().includes(q));
  });

  return (
    <div>
      <p className="text-sm text-[#4B5563] mb-4 max-w-2xl">Every registered customer. Click a row to see their full profile — contact, addresses, orders, bespoke orders, measurements and returns.</p>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input data-testid="customer-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, phone, ID…" className="flex-1 min-w-[240px] border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-squire-jeans" />
        <span className="text-xs text-[#9CA3AF]">{shown.length} customers</span>
      </div>

      {loading ? <div className="text-sm text-[#4B5563] py-8">Loading…</div> : shown.length === 0 ? (
        <div className="border border-black/10 rounded-lg p-10 text-center text-[#4B5563]">No customers found.</div>
      ) : (
        <div className="overflow-x-auto border border-black/10 rounded-lg">
          <table className="w-full text-sm">
            <thead><tr className="text-left sq-eyebrow bg-[#FAFAF8]"><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Orders</th><th className="px-4 py-3">Spent</th><th className="px-4 py-3">Balance</th><th className="px-4 py-3">Joined</th></tr></thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => setDetailId(c.id)} className="border-t border-black/10 hover:bg-squire-jeans/[0.04] cursor-pointer transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-squire-jeans/10 text-squire-jeans grid place-items-center font-display shrink-0">{(c.name || "U").slice(0, 1).toUpperCase()}</div>
                      <div><div className="font-medium">{c.name || "—"}</div><div className="text-[11px] text-[#9CA3AF]">{c.id}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><div>{c.email}</div><div className="text-[11px] text-[#9CA3AF]">{c.phone || "—"}</div></td>
                  <td className="px-4 py-3 font-semibold">{c.order_count}</td>
                  <td className="px-4 py-3">{formatINR(c.total_spent)}</td>
                  <td className="px-4 py-3">{c.balance_due > 0 ? <span className="text-amber-700">{formatINR(c.balance_due)}</span> : "—"}</td>
                  <td className="px-4 py-3 text-[#4B5563]">{(c.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CustomerDetailModal customerId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function UsersAdmin({ users, onChange }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "user" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const shown = (users || []).filter((u) => {
    if (roleFilter === "customers" && u.role !== "user") return false;
    if (roleFilter === "staff" && u.role === "user") return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return [u.name, u.email, u.phone, u.id].some((v) => String(v || "").toLowerCase().includes(q));
  });

  const create = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", phone: "", password: "", role: "user" });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const startEdit = (u) => { setEditingId(u.id); setEditForm({ name: u.name, email: u.email, phone: u.phone, role: u.role, password: "" }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/admin/users/${id}`, editForm);
      toast.success("User updated");
      cancelEdit();
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast("User deleted");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <div className="border border-black/10 p-5 mb-8 max-w-2xl">
        <div className="sq-eyebrow mb-3">New User</div>
        <div className="grid grid-cols-2 gap-4">
          <input data-testid="user-new-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          <input data-testid="user-new-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          <input data-testid="user-new-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          <input data-testid="user-new-password" placeholder="Password (optional)" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          <select data-testid="user-new-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            {USER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <button data-testid="user-create" onClick={create} className="sq-btn-primary mt-4">Add User</button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input data-testid="user-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, phone, ID…" className="flex-1 min-w-[220px] border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-squire-jeans" />
        <div className="flex gap-1">
          {["all", "customers", "staff"].map((r) => (
            <button key={r} data-testid={`user-filter-${r}`} onClick={() => setRoleFilter(r)} className={`px-3 py-2 text-xs uppercase tracking-[0.1em] border ${roleFilter === r ? "border-black bg-black text-white" : "border-black/15 hover:border-black"}`}>{r}</button>
          ))}
        </div>
        <span className="text-xs text-[#9CA3AF]">{shown.length} shown</span>
      </div>

      <table className="w-full text-sm">
        <thead><tr className="text-left sq-eyebrow"><th>ID</th><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th></th></tr></thead>
        <tbody>
          {shown.map((u) => (
            <tr key={u.id} className="border-t border-black/10 align-top">
              {editingId === u.id ? (
                <>
                  <td className="py-2">{u.id}</td>
                  <td><input data-testid={`user-edit-name-${u.id}`} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-transparent border-b border-black/30 outline-none text-sm w-full" /></td>
                  <td><input data-testid={`user-edit-email-${u.id}`} value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-transparent border-b border-black/30 outline-none text-sm w-full" /></td>
                  <td><input data-testid={`user-edit-phone-${u.id}`} value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="bg-transparent border-b border-black/30 outline-none text-sm w-full" /></td>
                  <td>
                    <select data-testid={`user-edit-role-${u.id}`} value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="bg-transparent border-b border-black/30 outline-none text-sm">
                      {USER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="whitespace-nowrap">
                    <button data-testid={`user-save-${u.id}`} onClick={() => saveEdit(u.id)} className="text-xs mr-3 hover:underline">Save</button>
                    <button data-testid={`user-cancel-${u.id}`} onClick={cancelEdit} className="text-xs text-[#4B5563] hover:underline">Cancel</button>
                  </td>
                </>
              ) : (
                <>
                  <td className="py-2">{u.id}</td><td>{u.name}</td><td>{u.email}</td><td>{u.phone}</td><td className="font-semibold">{u.role}</td>
                  <td className="whitespace-nowrap">
                    <button data-testid={`user-edit-${u.id}`} onClick={() => startEdit(u)} className="text-xs mr-3 hover:underline">Edit</button>
                    <button data-testid={`user-delete-${u.id}`} onClick={() => del(u.id)} className="text-xs text-[#4B5563] hover:underline">Delete</button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DEFAULT_CATEGORY_KEYS = ["fabric", "bespoke", "accessory"];

const PATTERN_TYPES = ["Plain", "Checked", "Strips", "Printed", "Solid", "Textured"];
const SLEEVE_TYPES = ["", "Half Sleeves", "Full Sleeves"];

// Product form — new product model. Order:
// Category → Sub-category → Name → Colour → Type/Pattern → Fabric (+image)
// → Sleeves → Sizes → Images (max 2) → Price (1 metre) → Best Seller.
function ProductForm({ value, onChange, onSubmit, submitLabel, testidPrefix }) {
  const [categories, setCategories] = useState([]);
  const [subOptions, setSubOptions] = useState([]);
  const [occasions, setOccasions] = useState([]);
  useEffect(() => {
    api.get("/product-categories", { params: { top_level: true } })
      .then(({ data }) => setCategories((data || []).filter((c) => c.key !== "customise")))
      .catch(() => setCategories([]));
    api.get("/seasonal-tiles").then(({ data }) => setOccasions(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!value.category) { setSubOptions([]); return; }
    api.get("/product-categories", { params: { parent_key: value.category } }).then(({ data }) => setSubOptions(data || [])).catch(() => setSubOptions([]));
  }, [value.category]);

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div>
      {/* 1. Category */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Category *</span>
        <select data-testid={`${testidPrefix}-category`} value={value.category} onChange={(e) => set({ category: e.target.value, sub_category: "" })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
          <option value="">Select category…</option>
          {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </label>

      {/* 2. Sub-category */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Sub-Category *</span>
        {subOptions.length > 0 ? (
          <select data-testid={`${testidPrefix}-sub_category`} value={value.sub_category} onChange={(e) => set({ sub_category: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            <option value="">Select sub-category…</option>
            {subOptions.map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
          </select>
        ) : (
          <input data-testid={`${testidPrefix}-sub_category`} value={value.sub_category} onChange={(e) => set({ sub_category: e.target.value })} placeholder={value.category ? "No sub-categories yet — add one under Product Categories, or type here" : "Pick a category first"} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        )}
      </label>

      {/* 3. Name */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Product Name *</span>
        <input data-testid={`${testidPrefix}-name`} value={value.name} onChange={(e) => set({ name: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>

      {/* 4. Colour */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Colour (comma for multiple)</span>
        <input data-testid={`${testidPrefix}-colors`} value={value.colors} onChange={(e) => set({ colors: e.target.value })} placeholder="e.g. White, Navy" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>

      {/* 5. Type / Pattern */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Type / Pattern</span>
        <select data-testid={`${testidPrefix}-pattern_type`} value={value.pattern_type || ""} onChange={(e) => set({ pattern_type: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
          <option value="">Select…</option>
          {PATTERN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>

      {/* 6. Fabric (name + optional image) */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Fabric</span>
        <input data-testid={`${testidPrefix}-fabric_name`} value={value.fabric_name || ""} onChange={(e) => set({ fabric_name: e.target.value })} placeholder="e.g. Egyptian Cotton" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <div className="mt-3">
        <ImageUploader value={value.fabric_image || ""} onChange={(img) => set({ fabric_image: img })} multiple={false} label="Fabric Image (optional)" testid={`${testidPrefix}-fabric_image`} />
      </div>

      {/* 7. Sleeves */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Sleeves (shirts etc.)</span>
        <select data-testid={`${testidPrefix}-sleeve_type`} value={value.sleeve_type || ""} onChange={(e) => set({ sleeve_type: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
          {SLEEVE_TYPES.map((t) => <option key={t} value={t}>{t || "Not applicable"}</option>)}
        </select>
      </label>

      {/* 8. Sizes */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Sizes (comma)</span>
        <input data-testid={`${testidPrefix}-sizes`} value={value.sizes} onChange={(e) => set({ sizes: e.target.value })} placeholder="e.g. S, M, L, XL" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>

      {/* 9. Images (max 2) */}
      <div className="mt-3">
        <ImageUploader value={value.images} onChange={(imgs) => set({ images: imgs })} multiple={true} max={2} label="Product Images (up to 2)" testid={`${testidPrefix}-images`} />
      </div>

      {/* 10. Price (per 1 metre) */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Price for 1 metre (₹) *</span>
        <input data-testid={`${testidPrefix}-price`} type="number" value={value.price} onChange={(e) => set({ price: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>

      {/* Description (optional) */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Description (optional)</span>
        <input data-testid={`${testidPrefix}-description`} value={value.description} onChange={(e) => set({ description: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>

      {/* Occasion (optional — Seasonal Spice link) */}
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Occasion / Season (optional)</span>
        <select data-testid={`${testidPrefix}-occasion`} value={value.occasion || ""} onChange={(e) => set({ occasion: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
          <option value="">None</option>
          {occasions.map((o) => <option key={o.slug} value={o.slug}>{o.name} ({o.slug})</option>)}
        </select>
      </label>

      {/* 11. Best seller */}
      <label className="flex items-center gap-2 mt-4">
        <input data-testid={`${testidPrefix}-best_seller`} type="checkbox" checked={!!value.best_seller} onChange={(e) => set({ best_seller: e.target.checked })} />
        <span className="text-xs">Mark as Best Seller</span>
      </label>
      <label className="flex items-center gap-2 mt-2">
        <input data-testid={`${testidPrefix}-customizable`} type="checkbox" checked={!!value.customizable} onChange={(e) => set({ customizable: e.target.checked })} />
        <span className="text-xs">Customizable (show Bespoke link)</span>
      </label>

      <button data-testid={`${testidPrefix}-submit`} onClick={onSubmit} className="sq-btn-primary w-full mt-5">{submitLabel}</button>
    </div>
  );
}

const BLANK_PRODUCT_FORM = { name: "", category: "", sub_category: "", price: "", description: "", images: [], sizes: "", colors: "", pattern_type: "", fabric_name: "", fabric_image: "", sleeve_type: "", best_seller: false, customizable: false, occasion: "" };

const CONFIG_GROUPS = [
  { key: "upper_body_shape", label: "Upper Body Shape (Shirt/Suit)" },
  { key: "lower_body_shape", label: "Lower Body Shape / Hip (Shirt/Trousers)" },
  { key: "shoulder_type", label: "Shoulder Type (Shirt/Suit)" },
  { key: "shirt_type", label: "Shirt Type" },
  { key: "fit", label: "Styling — Fit (Shirt)" },
  { key: "sleeve", label: "Styling — Sleeves (Shirt)" },
  { key: "pocket", label: "Styling — Pocket (Shirt)" },
  { key: "collar", label: "Styling — Collar (Shirt)" },
  { key: "placket", label: "Styling — Placket (Shirt)" },
  { key: "bottom_cut", label: "Styling — Bottom Cut (Shirt)" },
  { key: "cuff", label: "Styling — Cuff (Shirt)" },
  { key: "construction", label: "Construction (Suit)" },
  { key: "lapel", label: "Lapel (Suit)" },
  { key: "vent", label: "Vent (Suit)" },
  { key: "button_stance", label: "Button Stance (Suit)" },
  { key: "jacket_pocket", label: "Jacket Pocket (Suit)" },
  { key: "lining", label: "Lining (Suit)" },
  { key: "pleat", label: "Pleats (Suit/Trousers)" },
  { key: "hem", label: "Hem (Suit/Trousers)" },
  { key: "trouser_fit", label: "Fit (Trousers)" },
  { key: "trouser_pocket", label: "Pocket (Trousers)" },
  { key: "waistband", label: "Waistband (Trousers)" },
];

function ConfigOptionForm({ value, onChange, onSubmit, submitLabel, testidPrefix }) {
  return (
    <div>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Key (e.g. athletic)</span>
        <input data-testid={`${testidPrefix}-key`} value={value.key} onChange={(e) => onChange({ ...value, key: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Label (e.g. Athletic)</span>
        <input data-testid={`${testidPrefix}-label`} value={value.label} onChange={(e) => onChange({ ...value, label: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Price Add-on ₹ (0 if included)</span>
        <input data-testid={`${testidPrefix}-price`} type="number" value={value.price} onChange={(e) => onChange({ ...value, price: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Sort Order</span>
        <input data-testid={`${testidPrefix}-sort`} type="number" value={value.sort_order} onChange={(e) => onChange({ ...value, sort_order: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <div className="mt-3">
        <ImageUploader value={value.image} onChange={(img) => onChange({ ...value, image: img })} multiple={false} label="Option Image" testid={`${testidPrefix}-image`} />
      </div>
      <button data-testid={`${testidPrefix}-submit`} onClick={onSubmit} className="sq-btn-primary w-full mt-5">{submitLabel}</button>
    </div>
  );
}

const BLANK_CONFIG_OPTION_FORM = { key: "", label: "", image: "", price: 0, sort_order: 0 };

function Row({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-black/5 text-sm">
      <span className="text-[#4B5563]">{label}</span>
      <span className="text-right font-medium break-words">{String(value)}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="sq-eyebrow text-squire-jeans mb-1">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function OrderDetailModal({ orderId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shipping, setShipping] = useState(false);
  const reload = () => {
    setLoading(true);
    api.get(`/admin/orders/${orderId}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (!orderId) { setData(null); return; }
    reload();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const o = data?.order || {};
  const isBespoke = data?.kind === "bespoke";
  const meas = o.measurement || {};
  const style = o.style || {};
  const addr = o.address || {};

  const ship = async () => {
    setShipping(true);
    try { const { data: r } = await api.post(`/shiprocket/ship/${orderId}`); toast.success(`Shipped · AWB ${r.shiprocket_awb || "assigned"}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Shipping failed"); }
    finally { setShipping(false); }
  };
  const setStage = async (stage) => {
    try { await api.post(`/admin/orders/${orderId}/stage`, { stage }); toast.success(`Marked ${stage.replace(/_/g, " ")}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <Dialog open={!!orderId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="order-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-3">
            {orderId}
            {data && <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${isBespoke ? "bg-squire-jeans text-white" : "bg-black text-white"}`}>{isBespoke ? "Bespoke" : "Order"}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-[#4B5563] py-8">Loading…</div>}
        {data?.error && <div className="text-sm text-[#4B5563] py-8">Could not load this order.</div>}

        {data && !data.error && (
          <div className="mt-2">
            <Section title="Customer">
              <Row label="Name" value={data.customer?.name} />
              <Row label="Email" value={data.customer?.email} />
              <Row label="Phone" value={data.customer?.phone} />
              <Row label="User ID" value={o.user_id} />
            </Section>

            <Section title="Order Status">
              <Row label="Status" value={o.status} />
              {isBespoke && <Row label="Stage" value={o.stage} />}
              {o.stages?.length > 0 && <Row label="Stages" value={o.stages.join(" → ")} />}
              <Row label="Placed On" value={o.created_at?.replace("T", " ").slice(0, 16)} />
              <Row label="Estimated Delivery" value={(o.estimated_delivery || "").slice(0, 10)} />
              {o.shiprocket_tracking_id && <Row label="Tracking ID" value={o.shiprocket_tracking_id} />}
            </Section>

            <Section title="Karigar / Tailor Assignment">
              {data.karigar ? (
                <>
                  <Row label="Assigned To" value={data.karigar.name} />
                  <Row label="Specialty" value={data.karigar.specialty} />
                  <Row label="Karigar Phone" value={data.karigar.phone} />
                  <Row label="Allotted At" value={(o.allotted_at || "").replace("T", " ").slice(0, 16)} />
                </>
              ) : (
                <div className="text-sm text-[#4B5563] py-1">{isBespoke ? "Not yet allotted to a karigar." : "Not applicable for this order type."}</div>
              )}
            </Section>

            <Section title="Payment">
              <Row label="Payment Status" value={o.payment_status} />
              <Row label="Method / Gateway" value={o.payment_gateway || o.payment_method} />
              <Row label="Payment ID" value={o.razorpay_payment_id || o.payment_id} />
              <Row label="Razorpay Order" value={o.razorpay_order_id} />
            </Section>

            <Section title="Shipping (Shiprocket)">
              {o.shiprocket_awb || o.shiprocket_tracking_id ? (
                <>
                  <Row label="AWB / Tracking" value={o.shiprocket_awb || o.shiprocket_tracking_id} />
                  <Row label="Courier" value={o.courier} />
                  <Row label="Current Stage" value={(o.stage || "").replace(/_/g, " ")} />
                  {o.tracking_url && <div className="pt-2"><a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-sm text-squire-jeans underline">Track shipment ↗</a></div>}
                  <div className="flex flex-wrap gap-2 pt-3">
                    <button data-testid="order-mark-ofd" onClick={() => setStage("out_for_delivery")} disabled={o.stage === "delivered"} className="border border-black/20 hover:border-black px-3 py-1.5 text-[11px] uppercase tracking-[0.1em]">Out for Delivery</button>
                    <button data-testid="order-mark-delivered" onClick={() => setStage("delivered")} disabled={o.stage === "delivered"} className="border border-black bg-black text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] disabled:opacity-50">Mark Delivered</button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-[#4B5563]">{o.payment_status === "paid" || o.payment_status === "advance_paid" ? "Not shipped yet." : "Order must be paid before shipping."}</span>
                  <button data-testid="order-ship" onClick={ship} disabled={shipping || !(o.payment_status === "paid" || o.payment_status === "advance_paid")} className="sq-btn-primary !py-1.5 !px-3 !text-[11px]">{shipping ? "Shipping…" : "Ship via Shiprocket"}</button>
                </div>
              )}
            </Section>

            {data.fabric && (
              <Section title="Fabric">
                <div className="flex items-center gap-3 mb-2">
                  {data.fabric.image && <img src={data.fabric.image} alt="" className="w-12 h-12 object-cover bg-[#F5F5F5]" />}
                  <div>
                    <div className="font-medium text-sm">{data.fabric.name}</div>
                    <div className="text-xs text-[#4B5563]">{data.fabric.fabric_type} · {formatINR(data.fabric.price)}</div>
                  </div>
                </div>
              </Section>
            )}

            {isBespoke && (
              <>
                <Section title="Measurements">
                  {Object.entries(meas).filter(([k, v]) => v && k !== "profile_name").map(([k, v]) => (
                    <Row key={k} label={k.replace(/_/g, " ")} value={v} />
                  ))}
                  {Object.values(meas).filter((v) => v && v !== "Self").length === 0 && <div className="text-sm text-[#4B5563]">Send-us-a-garment / no manual measurements.</div>}
                </Section>
                <Section title="Style & Customization">
                  {Object.entries(style).filter(([, v]) => v).map(([k, v]) => (
                    <Row key={k} label={k.replace(/_/g, " ")} value={v} />
                  ))}
                </Section>
              </>
            )}

            {!isBespoke && o.items?.length > 0 && (
              <Section title={`Items (${o.items.length})`}>
                {o.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-black/5">
                    {it.product_snapshot?.image && <img src={it.product_snapshot.image} alt="" className="w-10 h-10 object-cover bg-[#F5F5F5]" />}
                    <div className="flex-1 text-sm">
                      <div className="font-medium">{it.product_snapshot?.name || it.product_id}</div>
                      <div className="text-xs text-[#4B5563]">Qty {it.quantity}{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""}</div>
                    </div>
                    <div className="text-sm">{formatINR(it.line_total)}</div>
                  </div>
                ))}
              </Section>
            )}

            <Section title="Delivery Address">
              {addr.line1 ? (
                <div className="text-sm">
                  {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
                  {addr.city}, {addr.state} — {addr.pincode}<br />
                  Phone: {addr.phone}
                </div>
              ) : <div className="text-sm text-[#4B5563]">No address on file.</div>}
            </Section>

            <Section title="Amount">
              <Row label="Fabric" value={o.fabric_cost != null ? formatINR(o.fabric_cost) : undefined} />
              <Row label="Subtotal" value={o.subtotal != null ? formatINR(o.subtotal) : undefined} />
              <Row label="Discount" value={o.discount ? `− ${formatINR(o.discount)}` : undefined} />
              <Row label={isBespoke ? "GST" : "Tax"} value={(o.gst ?? o.tax) != null ? formatINR(o.gst ?? o.tax) : undefined} />
              <Row label="Shipping" value={o.shipping != null ? (o.shipping > 0 ? formatINR(o.shipping) : "FREE") : undefined} />
              <div className="flex justify-between pt-2 mt-1 border-t border-black/10 font-display text-xl">
                <span>Total</span><span>{formatINR(o.total)}</span>
              </div>
              <Row label="Advance Paid" value={o.advance_paid != null ? formatINR(o.advance_paid) : undefined} />
              <Row label="Balance Due" value={o.balance_due != null ? (o.balance_due > 0 ? `${formatINR(o.balance_due)}${o.balance_method === "cod" ? " · COD" : ""}` : (o.balance_collected_cod ? "Collected (cash)" : "Paid in full")) : undefined} />
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const GUIDE_GARMENTS = [{ key: "shirt", label: "Shirt" }, { key: "suit", label: "Suit" }, { key: "trousers", label: "Trousers" }, { key: "blazer", label: "Blazer" }, { key: "kurta", label: "Kurta" }];

function PricingAdmin() {
  const [cfg, setCfg] = useState({ gst_rate: 18, free_shipping_threshold: 1200, shipping_charge: 100, advance_pct: 80 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/pricing-config").then(({ data }) => {
      setCfg({
        gst_rate: String(Math.round((data.gst_rate ?? 0.18) * 10000) / 100),
        free_shipping_threshold: String(data.free_shipping_threshold ?? 1200),
        shipping_charge: String(data.shipping_charge ?? 100),
        advance_pct: String(Math.round((data.advance_pct ?? 0.8) * 100)),
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    try {
      await api.put("/pricing-config", {
        gst_rate: (parseFloat(cfg.gst_rate) || 0) / 100,
        free_shipping_threshold: parseFloat(cfg.free_shipping_threshold) || 0,
        shipping_charge: parseFloat(cfg.shipping_charge) || 0,
        advance_pct: (parseFloat(cfg.advance_pct) || 0) / 100,
        stitching: {}, fabric_multiplier: {},
      });
      toast.success("Pricing saved");
    } catch (e) { toast.error("Save failed"); }
  };
  const setK = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });
  const fieldCls = "w-32 bg-transparent border-b border-black/30 outline-none py-1 text-sm";

  return (
    <div className="mb-10 border border-black/10 p-5" data-testid="admin-pricing">
      <div className="sq-eyebrow text-squire-jeans">Bespoke Pricing</div>
      <p className="text-sm text-[#4B5563] mt-1 mb-4 max-w-2xl">Bespoke price = fabric + GST. Shipping is free at/above the threshold, otherwise the shipping charge applies. Customers pay the advance % up front and the balance on delivery. These drive every Bespoke page.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <label className="block"><span className="sq-eyebrow text-[10px]">GST Rate</span>
          <div className="flex items-center gap-1"><input data-testid="pricing-gst_rate" type="number" value={cfg.gst_rate} onChange={setK("gst_rate")} className={fieldCls} /><span className="text-xs text-[#4B5563]">%</span></div>
        </label>
        <label className="block"><span className="sq-eyebrow text-[10px]">Free Shipping Above</span>
          <div className="flex items-center gap-1"><span className="text-xs text-[#4B5563]">₹</span><input data-testid="pricing-free_shipping_threshold" type="number" value={cfg.free_shipping_threshold} onChange={setK("free_shipping_threshold")} className={fieldCls} /></div>
        </label>
        <label className="block"><span className="sq-eyebrow text-[10px]">Shipping Charge</span>
          <div className="flex items-center gap-1"><span className="text-xs text-[#4B5563]">₹</span><input data-testid="pricing-shipping_charge" type="number" value={cfg.shipping_charge} onChange={setK("shipping_charge")} className={fieldCls} /></div>
        </label>
        <label className="block"><span className="sq-eyebrow text-[10px]">Advance (pay now)</span>
          <div className="flex items-center gap-1"><input data-testid="pricing-advance_pct" type="number" value={cfg.advance_pct} onChange={setK("advance_pct")} className={fieldCls} /><span className="text-xs text-[#4B5563]">%</span></div>
        </label>
      </div>
      <button data-testid="pricing-save" onClick={save} disabled={!loaded} className="sq-btn-primary mt-5">Save Pricing</button>
    </div>
  );
}

function BespokeGuideAdmin() {
  const [garment, setGarment] = useState("shirt");
  const [form, setForm] = useState({ youtube_url: "", images: [], intro: "" });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setLoaded(false);
    api.get("/bespoke-guide", { params: { garment } }).then(({ data }) => { setForm({ youtube_url: data.youtube_url || "", images: data.images || [], intro: data.intro || "" }); setLoaded(true); }).catch(() => setLoaded(true));
  }, [garment]);
  const save = async () => {
    try { await api.put("/bespoke-guide", { ...form, images: form.images.slice(0, 5) }, { params: { garment } }); toast.success(`${garment} guide saved`); }
    catch (e) { toast.error("Save failed"); }
  };
  return (
    <div className="mb-10 border border-black/10 p-5" data-testid="admin-bespoke-guide">
      <div className="sq-eyebrow text-squire-jeans">Bespoke — Measurement Guide</div>
      <p className="text-sm text-[#4B5563] mt-1 mb-4 max-w-2xl">The YouTube video and up to 5 images shown in each Bespoke garment's "Measure it yourself" step. Pick a garment, edit, and save — each garment has its own guide.</p>
      <div className="flex gap-2 mb-4">
        {GUIDE_GARMENTS.map((g) => (
          <button key={g.key} data-testid={`guide-garment-${g.key}`} onClick={() => setGarment(g.key)} className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border ${garment === g.key ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>{g.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block"><span className="sq-eyebrow text-[10px]">YouTube Video URL</span>
            <input data-testid="guide-youtube" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
          <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Caption / Intro (optional)</span>
            <input data-testid="guide-intro" value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
        </div>
        <div>
          <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} multiple={true} max={5} label="Guide Images (up to 5)" testid="guide-images" />
        </div>
      </div>
      <button data-testid="guide-save" onClick={save} disabled={!loaded} className="sq-btn-primary mt-5">Save {GUIDE_GARMENTS.find((g) => g.key === garment)?.label} Guide</button>
    </div>
  );
}

const BESPOKE_GARMENT_LIST = [
  { key: "shirt", label: "Shirt" },
  { key: "trousers", label: "Trousers" },
  { key: "suit", label: "Suit" },
  { key: "blazer", label: "Blazer" },
  { key: "kurta", label: "Kurta" },
  { key: "jeans", label: "Jeans" },
];
const slugKey = (s) => String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

function GarmentSectionsAdmin() {
  const [garment, setGarment] = useState("trousers");
  const [sections, setSections] = useState([]); // [{key,required,advanced}]
  const [records, setRecords] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addKey, setAddKey] = useState("");

  // Real garments = core list + any admin-created bespoke categories (by their real key).
  const garments = [...BESPOKE_GARMENT_LIST, ...cats.filter((c) => !BESPOKE_GARMENT_LIST.some((g) => g.key === c.key)).map((c) => ({ key: c.key, label: c.label || c.key }))];

  const overrides = Object.fromEntries(records.filter((r) => r.builtin).map((r) => [r.key, r]));
  const custom = records.filter((r) => !r.builtin);
  const catalog = [
    ...CONFIG_GROUPS.filter((g) => !overrides[g.key]?.deleted).map((g) => ({ key: g.key, label: overrides[g.key]?.label || g.label })),
    ...custom.map((g) => ({ key: g.key, label: g.label })),
  ];
  const labelFor = (key) => catalog.find((c) => c.key === key)?.label || key;
  const available = catalog.filter((c) => !sections.some((s) => s.key === c.key));

  const loadRecords = () => api.get("/config-option-groups").then(({ data }) => setRecords(data || [])).catch(() => {});
  const loadSections = (g) => { setLoading(true); api.get(`/bespoke-option-groups/${g}`).then(({ data }) => setSections((data.option_groups || []).map((x) => ({ key: x.key, required: !!x.required, advanced: !!x.advanced })))).catch(() => setSections([])).finally(() => setLoading(false)); };
  useEffect(() => { loadRecords(); api.get("/bespoke-categories").then(({ data }) => setCats(data || [])).catch(() => {}); }, []);
  useEffect(() => { loadSections(garment); }, [garment]);

  const add = () => { if (!addKey) return; setSections((s) => [...s, { key: addKey, required: false, advanced: false }]); setAddKey(""); };
  const remove = (i) => setSections((s) => s.filter((_, idx) => idx !== i));
  const move = (i, dir) => setSections((s) => { const n = [...s]; const j = i + dir; if (j < 0 || j >= n.length) return n; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const upd = (i, patch) => setSections((s) => s.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/bespoke-option-groups/${garment}`, { option_groups: sections.map((s) => ({ key: s.key, label: labelFor(s.key), required: s.required, advanced: s.advanced, options: [] })) }); toast.success("Saved — live on the bespoke page"); }
    catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };
  const resetDefaults = async () => {
    if (!window.confirm("Reset this garment to its built-in default sections?")) return;
    try { await api.put(`/bespoke-option-groups/${garment}`, { option_groups: [] }); setSections([]); toast("Reset to built-in defaults"); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="mt-12 border-t border-black/10 pt-8">
      <div className="sq-eyebrow mb-1">Bespoke Page Sections</div>
      <p className="text-sm text-[#4B5563] mb-5 max-w-3xl">Control which option sections appear on each garment's bespoke page — and in what order. Pick the Configurator Option Groups you created above. Removing every section here falls back to that garment's built-in default layout. Options and images for each section are managed in the panel above.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {garments.map((g) => (
          <button key={g.key} data-testid={`sections-garment-${g.key}`} onClick={() => setGarment(g.key)}
            className={`px-4 py-2 text-sm border ${garment === g.key ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>{g.label}</button>
        ))}
      </div>

      {loading ? <div className="text-sm text-[#4B5563]">Loading…</div> : (
        <div className="max-w-2xl">
          {sections.length === 0 && <div className="border border-dashed border-black/20 rounded-lg p-6 text-center text-[#4B5563] text-sm">No custom sections — this garment uses its built-in default layout. Add sections below to override it.</div>}

          <div className="space-y-2">
            {sections.map((s, i) => (
              <div key={s.key} className="flex items-center gap-3 border border-black/10 rounded-lg p-3" data-testid={`section-row-${s.key}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[#9CA3AF] hover:text-black disabled:opacity-30 leading-none">▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} className="text-[#9CA3AF] hover:text-black disabled:opacity-30 leading-none">▼</button>
                </div>
                <div className="flex-1 font-medium">{labelFor(s.key)} <span className="text-[11px] text-[#9CA3AF]">({s.key})</span></div>
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={s.required} onChange={(e) => upd(i, { required: e.target.checked })} /> Required</label>
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={s.advanced} onChange={(e) => upd(i, { advanced: e.target.checked })} /> Advanced</label>
                <button onClick={() => remove(i)} data-testid={`section-remove-${s.key}`} className="text-xs text-red-600 hover:underline">Remove</button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <select data-testid="section-add-select" value={addKey} onChange={(e) => setAddKey(e.target.value)} className="border border-black/20 rounded px-3 py-2 text-sm outline-none focus:border-squire-jeans">
              <option value="">Add a section…</option>
              {available.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <button onClick={add} data-testid="section-add" disabled={!addKey} className="border border-black/25 hover:border-black px-3 py-2 text-sm disabled:opacity-40">Add</button>
            <button onClick={save} data-testid="sections-save" disabled={saving} className="sq-btn-primary">{saving ? "Saving…" : "Save Sections"}</button>
            <button onClick={resetDefaults} className="text-xs text-[#4B5563] hover:text-black underline">Reset to defaults</button>
          </div>

          <p className="text-[11px] text-[#9CA3AF] mt-3">Tip: mark a section <strong>Advanced</strong> to place it inside the "Advanced Customization" dropdown on the bespoke page.</p>
        </div>
      )}
    </div>
  );
}

function MeasureFieldsAdmin() {
  const [garment, setGarment] = useState("shirt");
  const [fields, setFields] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const garments = [...BESPOKE_GARMENT_LIST, ...cats.filter((c) => !BESPOKE_GARMENT_LIST.some((g) => g.key === c.key)).map((c) => ({ key: c.key, label: c.label || c.key }))];

  const load = (g) => { setLoading(true); api.get(`/bespoke-measure-fields/${g}`).then(({ data }) => setFields(data.fields || [])).catch(() => setFields([])).finally(() => setLoading(false)); };
  useEffect(() => { load(garment); }, [garment]);
  useEffect(() => { api.get("/bespoke-categories").then(({ data }) => setCats(data || [])).catch(() => {}); }, []);

  const addField = () => setFields((f) => [...f, { key: "", label: "", unit: "in", required: false }]);
  const upd = (i, patch) => setFields((f) => f.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const del = (i) => setFields((f) => f.filter((_, idx) => idx !== i));
  const move = (i, dir) => setFields((f) => { const n = [...f]; const j = i + dir; if (j < 0 || j >= n.length) return n; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const save = async () => {
    const clean = fields.filter((f) => f.label.trim()).map((f) => ({ key: f.key || slugKey(f.label), label: f.label.trim(), unit: f.unit || "in", required: !!f.required }));
    setSaving(true);
    try { await api.put(`/bespoke-measure-fields/${garment}`, { fields: clean }); toast.success("Measurement fields saved"); setFields(clean); }
    catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="mt-12 border-t border-black/10 pt-8">
      <div className="sq-eyebrow mb-1">Measurement Fields</div>
      <p className="text-sm text-[#4B5563] mb-5 max-w-3xl">Control the "Measure it yourself" fields shown on each garment's bespoke page — add, edit, reorder or remove them, set each as Required, and pick its unit (inch / cm / feet). Body Height is always shown as a feet + inch picker.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {garments.map((g) => (
          <button key={g.key} data-testid={`measure-garment-${g.key}`} onClick={() => setGarment(g.key)}
            className={`px-4 py-2 text-sm border ${garment === g.key ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>{g.label}</button>
        ))}
      </div>

      {loading ? <div className="text-sm text-[#4B5563]">Loading…</div> : (
        <div className="max-w-3xl">
          {fields.length === 0 && <div className="border border-dashed border-black/20 rounded-lg p-6 text-center text-[#4B5563] text-sm">No measurement fields yet — add some below (Body Height is always shown automatically).</div>}
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={i} className="flex items-center gap-3 border border-black/10 rounded-lg p-3" data-testid={`measure-field-${i}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[#9CA3AF] hover:text-black disabled:opacity-30 leading-none">▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === fields.length - 1} className="text-[#9CA3AF] hover:text-black disabled:opacity-30 leading-none">▼</button>
                </div>
                <input data-testid={`measure-field-${i}-label`} placeholder="Field label (e.g. Chest)" value={f.label} onChange={(e) => upd(i, { label: e.target.value, key: f.key || slugKey(e.target.value) })} className="flex-1 bg-transparent border-b border-black/20 outline-none py-1 text-sm" />
                <select data-testid={`measure-field-${i}-unit`} value={f.unit} onChange={(e) => upd(i, { unit: e.target.value })} className="border border-black/20 rounded px-2 py-1.5 text-sm outline-none">
                  <option value="in">inch</option>
                  <option value="cm">cm</option>
                  <option value="ft">feet</option>
                </select>
                <label className="flex items-center gap-1.5 text-xs whitespace-nowrap"><input type="checkbox" checked={!!f.required} onChange={(e) => upd(i, { required: e.target.checked })} /> Required</label>
                <button onClick={() => del(i)} data-testid={`measure-field-${i}-remove`} className="text-xs text-red-600 hover:underline">Remove</button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={addField} data-testid="measure-add" className="border border-black/25 hover:border-black px-3 py-2 text-sm">+ Add field</button>
            <button onClick={save} data-testid="measure-save" disabled={saving} className="sq-btn-primary">{saving ? "Saving…" : "Save Fields"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigOptionsAdmin() {
  const [records, setRecords] = useState([]); // all config_option_groups rows (custom + builtin overrides)
  const overrides = Object.fromEntries(records.filter((r) => r.builtin).map((r) => [r.key, r]));
  const customGroups = records.filter((r) => !r.builtin);
  const hiddenBuiltins = CONFIG_GROUPS.filter((g) => overrides[g.key]?.deleted);
  const builtinList = CONFIG_GROUPS
    .filter((g) => !overrides[g.key]?.deleted)
    .map((g) => ({ key: g.key, label: overrides[g.key]?.label || g.label, builtin: true }));
  const allGroups = [...builtinList, ...customGroups.map((g) => ({ key: g.key, label: g.label, builtin: false, _id: g.id }))];

  const [group, setGroup] = useState(CONFIG_GROUPS[0].key);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK_CONFIG_OPTION_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_CONFIG_OPTION_FORM);
  const [newGroup, setNewGroup] = useState({ label: "", key: "" });
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [editGroup, setEditGroup] = useState(null); // {builtin, id?, key, label}

  const loadGroups = () => api.get("/config-option-groups").then(({ data }) => setRecords(data || [])).catch(() => setRecords([]));
  useEffect(() => { loadGroups(); }, []);

  const load = async (g) => {
    const { data } = await api.get("/config-options", { params: { group: g } });
    setItems(data);
  };
  useEffect(() => { load(group); setEditingId(null); setEditGroup(null); }, [group]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCustom = customGroups.some((g) => g.key === group);
  const isBuiltin = CONFIG_GROUPS.some((g) => g.key === group);

  const createGroup = async () => {
    const key = (newGroup.key || newGroup.label).toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!newGroup.label || !key) { toast.error("Enter a group name"); return; }
    try {
      const { data } = await api.post("/config-option-groups", { key, label: newGroup.label, sort_order: 100 });
      toast.success("Option group created");
      setNewGroup({ label: "", key: "" }); setShowNewGroup(false);
      await loadGroups();
      setGroup(data.key);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create group"); }
  };

  const startEditGroup = () => {
    const g = allGroups.find((x) => x.key === group);
    if (!g) return;
    setEditGroup({ builtin: g.builtin, id: g._id, key: g.key, label: g.label });
    setShowNewGroup(false);
  };
  const saveGroupEdit = async () => {
    if (!editGroup?.label?.trim()) { toast.error("Group name is required"); return; }
    try {
      if (editGroup.builtin) {
        // built-in: only the label can change (key is fixed so configs keep working)
        await api.put(`/config-option-groups/builtin/${editGroup.key}`, { label: editGroup.label });
        await loadGroups(); setEditGroup(null);
      } else {
        const key = (editGroup.key || editGroup.label).toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        const { data } = await api.put(`/config-option-groups/${editGroup.id}`, { label: editGroup.label, key });
        await loadGroups(); setEditGroup(null); setGroup(data.key);
      }
      toast.success("Group updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const deleteGroup = async () => {
    const g = allGroups.find((x) => x.key === group);
    if (!g) return;
    if (!window.confirm(`Delete the "${g.label}" group and all its options?`)) return;
    try {
      if (g.builtin) await api.delete(`/config-option-groups/builtin/${g.key}`);
      else await api.delete(`/config-option-groups/${g._id}`);
      toast("Group deleted");
      await loadGroups();
      setGroup(CONFIG_GROUPS.find((x) => x.key !== g.key)?.key || CONFIG_GROUPS[0].key);
    } catch (e) { toast.error("Delete failed"); }
  };
  const restoreBuiltin = async (key) => {
    try { await api.post(`/config-option-groups/builtin/${key}/restore`); toast.success("Group restored"); await loadGroups(); setGroup(key); }
    catch (e) { toast.error("Restore failed"); }
  };

  const create = async () => {
    if (!form.key || !form.label || !form.image) { toast.error("Key, label and image are required"); return; }
    try {
      await api.post("/config-options", { ...form, group, price: Number(form.price) || 0, sort_order: Number(form.sort_order) || 0 });
      toast.success("Option added");
      setForm(BLANK_CONFIG_OPTION_FORM);
      load(group);
    } catch (e) { toast.error("Create failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this option?")) return;
    await api.delete(`/config-options/${id}`); toast("Deleted"); load(group);
  };

  const startEdit = (it) => { setEditingId(it.id); setEditForm({ key: it.key, label: it.label, image: it.image, price: it.price || 0, sort_order: it.sort_order || 0 }); };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_CONFIG_OPTION_FORM); };
  const saveEdit = async (id) => {
    if (!editForm.key || !editForm.label || !editForm.image) { toast.error("Key, label and image are required"); return; }
    try {
      await api.put(`/config-options/${id}`, { ...editForm, group, price: Number(editForm.price) || 0, sort_order: Number(editForm.sort_order) || 0 });
      toast.success("Option updated");
      cancelEdit();
      load(group);
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <div className="mt-6">
      <div className="sq-eyebrow mb-3">Configurator Option Group</div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          data-testid="config-options-group"
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          className="bg-transparent border border-black/20 rounded px-3 py-2 text-sm outline-none focus:border-squire-jeans"
        >
          <optgroup label="Built-in groups">
            {builtinList.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </optgroup>
          {customGroups.length > 0 && (
            <optgroup label="Custom groups">
              {customGroups.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </optgroup>
          )}
        </select>

        <button data-testid="config-group-new-toggle" onClick={() => { setShowNewGroup((s) => !s); setEditGroup(null); }} className="border border-black/25 hover:border-black rounded px-3 py-2 text-sm">+ New Group</button>
        {(isCustom || isBuiltin) && (
          <>
            <button data-testid="config-group-edit" onClick={startEditGroup} className="text-xs border border-black/25 hover:border-black rounded px-3 py-2">Edit group</button>
            <button data-testid="config-group-delete" onClick={deleteGroup} className="text-xs text-red-600 hover:underline">Delete this group</button>
          </>
        )}
      </div>

      {hiddenBuiltins.length > 0 && (
        <div className="text-xs text-[#4B5563] mb-4" data-testid="config-hidden-builtins">
          Hidden built-in groups:{" "}
          {hiddenBuiltins.map((g, i) => (
            <span key={g.key}>
              {i > 0 && ", "}
              {g.label} <button onClick={() => restoreBuiltin(g.key)} className="text-squire-jeans underline">restore</button>
            </span>
          ))}
        </div>
      )}

      {editGroup && (
        <div className="border border-black/15 rounded-lg p-4 mb-6 max-w-xl bg-[#FAFAF8]" data-testid="config-group-edit-form">
          <div className="sq-eyebrow mb-2">Edit Option Group{editGroup.builtin ? " (built-in)" : ""}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block"><span className="sq-eyebrow text-[10px]">Group Name</span>
              <input data-testid="config-group-edit-label" value={editGroup.label} onChange={(e) => setEditGroup({ ...editGroup, label: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
            </label>
            <label className="block"><span className="sq-eyebrow text-[10px]">Key{editGroup.builtin ? " (fixed)" : ""}</span>
              <input data-testid="config-group-edit-key" value={editGroup.key} disabled={editGroup.builtin} onChange={(e) => setEditGroup({ ...editGroup, key: e.target.value })} className={`w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm ${editGroup.builtin ? "text-[#9CA3AF]" : ""}`} />
            </label>
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="config-group-edit-save" onClick={saveGroupEdit} className="sq-btn-primary">Save Changes</button>
            <button onClick={() => setEditGroup(null)} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-3">{editGroup.builtin ? "Built-in groups keep their key so the bespoke pages keep working — only the display name changes." : "Renaming the key moves this group's existing options along with it."}</p>
        </div>
      )}

      {showNewGroup && (
        <div className="border border-black/15 rounded-lg p-4 mb-6 max-w-xl bg-[#FAFAF8]" data-testid="config-group-new-form">
          <div className="sq-eyebrow mb-2">New Configurator Option Group</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block"><span className="sq-eyebrow text-[10px]">Group Name (e.g. Trouser Type)</span>
              <input data-testid="config-group-new-label" value={newGroup.label} onChange={(e) => setNewGroup({ label: e.target.value, key: newGroup.key })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
            </label>
            <label className="block"><span className="sq-eyebrow text-[10px]">Key (optional — auto from name)</span>
              <input data-testid="config-group-new-key" value={newGroup.key} onChange={(e) => setNewGroup({ label: newGroup.label, key: e.target.value })} placeholder="trouser_type" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
            </label>
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="config-group-new-create" onClick={createGroup} className="sq-btn-primary">Create Group</button>
            <button onClick={() => { setShowNewGroup(false); setNewGroup({ label: "", key: "" }); }} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-3">After creating, add its options (image + label + price) on the right, just like any other group.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-2">
          {items.length === 0 && <div className="text-sm text-[#4B5563]">No options added yet for this group — the storefront falls back to a default icon set until you add some here.</div>}
          {items.map((it) => (
            editingId === it.id ? (
              <div key={it.id} className="border border-black/10 p-4" data-testid={`config-option-edit-${it.id}`}>
                <ConfigOptionForm value={editForm} onChange={setEditForm} onSubmit={() => saveEdit(it.id)} submitLabel="Save Changes" testidPrefix={`config-option-edit-${it.id}`} />
                <button data-testid={`config-option-cancel-${it.id}`} onClick={cancelEdit} className="w-full text-center text-xs text-[#4B5563] hover:text-black mt-3">Cancel</button>
              </div>
            ) : (
              <div key={it.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`config-option-${it.id}`}>
                <img src={it.image} alt="" className="w-14 h-14 object-contain bg-[#F5F5F5]" />
                <div className="flex-1">
                  <div className="font-display text-lg">{it.label}</div>
                  <div className="sq-eyebrow">{it.key} · order {it.sort_order}{it.price > 0 ? ` · +${formatINR(it.price)}` : ""}</div>
                </div>
                <button data-testid={`config-option-edit-${it.id}`} onClick={() => startEdit(it)} className="text-xs mr-3 hover:underline">Edit</button>
                <button data-testid={`config-option-delete-${it.id}`} onClick={() => del(it.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
              </div>
            )
          ))}
        </div>
        <div className="border border-black/10 p-5 h-fit">
          <div className="sq-eyebrow">New Option</div>
          <ConfigOptionForm value={form} onChange={setForm} onSubmit={create} submitLabel="Add Option" testidPrefix="config-option-new" />
        </div>
      </div>
    </div>
  );
}

const BLANK_BESPOKE_CATEGORY_FORM = { key: "", label: "", hub_label: "", hub_subtitle: "", image: "", tagline: "", price: 0, description: "", sort_order: 0 };

function BespokeCategoryFields({ value, onChange, prefix }) {
  return (
    <>
      {[["key", "Key (e.g. sherwani)"], ["label", "Title"], ["hub_label", "Hub Card Title (e.g. Custom Sherwani)"], ["hub_subtitle", "Hub Card Subtitle"], ["tagline", "Tagline"], ["price", "Starting Price ₹"], ["description", "Description"], ["sort_order", "Sort Order"]].map(([k, l]) => (
        <label key={k} className="block mt-3">
          <span className="sq-eyebrow text-[10px]">{l}</span>
          <input data-testid={`${prefix}-${k}`} value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </label>
      ))}
      <div className="mt-3">
        <ImageUploader value={value.image} onChange={(img) => onChange({ ...value, image: img })} multiple={false} label="Category Image" testid={`${prefix}-image`} />
      </div>
    </>
  );
}

function BespokeCategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK_BESPOKE_CATEGORY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_BESPOKE_CATEGORY_FORM);

  const load = () => api.get("/bespoke-categories").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.key || !form.label || !form.image) { toast.error("Key, label and image are required"); return; }
    try {
      await api.post("/bespoke-categories", { ...form, price: Number(form.price) || 0, sort_order: Number(form.sort_order) || 0 });
      toast.success("Bespoke category added");
      setForm(BLANK_BESPOKE_CATEGORY_FORM);
      load();
    } catch (e) { toast.error("Create failed — key may already be in use"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this bespoke category?")) return;
    await api.delete(`/bespoke-categories/${id}`); toast("Deleted"); load();
  };
  const startEdit = (c) => { setEditingId(c.id); setEditForm({ ...BLANK_BESPOKE_CATEGORY_FORM, ...c }); };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_BESPOKE_CATEGORY_FORM); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/bespoke-categories/${id}`, { ...editForm, price: Number(editForm.price) || 0, sort_order: Number(editForm.sort_order) || 0 });
      toast.success("Bespoke category updated");
      cancelEdit();
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <div className="mt-6">
      <p className="text-sm text-[#4B5563] mb-6 max-w-2xl">Shirt, Suit and Trousers have their own dedicated configurator pages. Anything you add here shows as an extra card on the Bespoke hub and gets a general-purpose configurator page (fabric, profile, measurements) at /bespoke/&lt;key&gt;.</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-2">
          {items.length === 0 && <div className="text-sm text-[#4B5563]">No extra bespoke categories yet.</div>}
          {items.map((c) => (
            editingId === c.id ? (
              <div key={c.id} className="border border-black/10 p-4" data-testid={`bespoke-category-edit-${c.id}`}>
                <BespokeCategoryFields value={editForm} onChange={setEditForm} prefix={`bespoke-cat-edit-${c.id}`} />
                <div className="flex gap-3 mt-4">
                  <button data-testid={`bespoke-cat-save-${c.id}`} onClick={() => saveEdit(c.id)} className="sq-btn-primary flex-1">Save Changes</button>
                  <button data-testid={`bespoke-cat-cancel-${c.id}`} onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
                </div>
              </div>
            ) : (
              <div key={c.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`bespoke-category-${c.id}`}>
                <img src={c.image} alt="" className="w-14 h-14 object-contain bg-[#F5F5F5]" />
                <div className="flex-1">
                  <div className="font-display text-lg">{c.hub_label || c.label}</div>
                  <div className="sq-eyebrow">{c.key} · from {formatINR(c.price)}</div>
                </div>
                <button data-testid={`bespoke-category-edit-${c.id}`} onClick={() => startEdit(c)} className="text-xs mr-3 hover:underline">Edit</button>
                <button data-testid={`bespoke-category-delete-${c.id}`} onClick={() => del(c.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
              </div>
            )
          ))}
        </div>
        <div className="border border-black/10 p-5 h-fit">
          <div className="sq-eyebrow">New Bespoke Category</div>
          <BespokeCategoryFields value={form} onChange={setForm} prefix="bespoke-cat-new" />
          <button data-testid="bespoke-cat-create" onClick={create} className="sq-btn-primary w-full mt-5">Add Category</button>
        </div>
      </div>
    </div>
  );
}

const BLANK_PRODUCT_CATEGORY_FORM = { key: "", label: "", image: "", parent_key: "", route: "", sort_order: 0, in_products_menu: true, in_home_houses: false };

const CATEGORY_NAMED_ROUTES = { fabric: "/fabrics", accessory: "/accessories" };
function deriveSubCategoryRoute(parentKey, subKey) {
  if (parentKey === "fabric") return `/fabrics?type=${subKey}`;
  if (parentKey === "bespoke") return `/bespoke/${subKey}`;
  const base = CATEGORY_NAMED_ROUTES[parentKey] || `/shop/${parentKey}`;
  return `${base}?sub=${subKey}`;
}

function ProductCategoriesAdmin() {
  const [topLevels, setTopLevels] = useState([]);
  const [activeParent, setActiveParent] = useState("");
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState(BLANK_PRODUCT_CATEGORY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_PRODUCT_CATEGORY_FORM);

  const [catProducts, setCatProducts] = useState([]);
  const [activeSubFilter, setActiveSubFilter] = useState("");
  const [editingProductId, setEditingProductId] = useState(null);
  const [editProductForm, setEditProductForm] = useState(BLANK_PRODUCT_FORM);
  const [newProductForm, setNewProductForm] = useState(BLANK_PRODUCT_FORM);

  const loadTop = () => api.get("/product-categories", { params: { top_level: true } }).then(({ data }) => setTopLevels(data));
  const loadSubs = (parent) => api.get("/product-categories", { params: { parent_key: parent } }).then(({ data }) => setSubs(data));
  const [childCounts, setChildCounts] = useState({});
  const loadCounts = () => api.get("/product-categories").then(({ data }) => {
    const m = {}; (data || []).forEach((c) => { if (c.parent_key) m[c.parent_key] = (m[c.parent_key] || 0) + 1; });
    setChildCounts(m);
  }).catch(() => {});
  const loadCatProducts = (parent, subFilter) => {
    const params = { category: parent, limit: 200 };
    if (subFilter) params.sub_category = subFilter;
    api.get("/products", { params }).then(({ data }) => setCatProducts(data.items || []));
  };
  useEffect(() => { loadTop(); loadCounts(); }, []);
  useEffect(() => {
    if (activeParent) { loadSubs(activeParent); setActiveSubFilter(""); }
    else setSubs([]);
  }, [activeParent]);
  useEffect(() => {
    if (activeParent) loadCatProducts(activeParent, activeSubFilter);
    else setCatProducts([]);
  }, [activeParent, activeSubFilter]);
  useEffect(() => {
    setNewProductForm({ ...BLANK_PRODUCT_FORM, category: activeParent || "", sub_category: activeSubFilter || "" });
  }, [activeParent, activeSubFilter]);

  const createTop = async () => {
    if (!form.key || !form.label || !form.image) { toast.error("Key, label and image are required"); return; }
    try {
      await api.post("/product-categories", { key: form.key, label: form.label, image: form.image, parent_key: null, route: form.route || null, in_products_menu: form.in_products_menu !== false, in_home_houses: !!form.in_home_houses, sort_order: Number(form.sort_order) || 0 });
      toast.success("Category added");
      setForm(BLANK_PRODUCT_CATEGORY_FORM);
      loadTop();
    } catch (e) { toast.error("Create failed — key may already be in use"); }
  };
  const createSub = async () => {
    if (!activeParent) { toast.error("Pick a top-level category first"); return; }
    if (!form.key || !form.label) { toast.error("Key and label are required"); return; }
    try {
      await api.post("/product-categories", { key: form.key, label: form.label, image: form.image, parent_key: activeParent, route: deriveSubCategoryRoute(activeParent, form.key), sort_order: Number(form.sort_order) || 0 });
      toast.success("Sub-category added");
      setForm(BLANK_PRODUCT_CATEGORY_FORM);
      loadSubs(activeParent); loadCounts();
    } catch (e) { toast.error("Create failed — key may already be in use"); }
  };
  const del = async (id, isTop) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/product-categories/${id}`); toast("Deleted");
    if (isTop) loadTop(); else loadSubs(activeParent);
    loadCounts();
  };
  const startEdit = (c) => { setEditingId(c.id); setEditForm({ ...BLANK_PRODUCT_CATEGORY_FORM, ...c, parent_key: c.parent_key || "" }); };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_PRODUCT_CATEGORY_FORM); };
  const saveEdit = async (id, isTop) => {
    try {
      const route = isTop ? (editForm.route || null) : deriveSubCategoryRoute(activeParent, editForm.key);
      await api.put(`/product-categories/${id}`, { ...editForm, parent_key: isTop ? null : activeParent, route, in_products_menu: isTop ? (editForm.in_products_menu !== false) : false, in_home_houses: isTop ? !!editForm.in_home_houses : false, sort_order: Number(editForm.sort_order) || 0 });
      toast.success("Updated");
      cancelEdit();
      if (isTop) loadTop(); else loadSubs(activeParent);
    } catch (e) { toast.error("Update failed"); }
  };

  const startEditProduct = (p) => {
    setEditingProductId(p.id);
    setEditProductForm({
      name: p.name || "", category: p.category || activeParent, sub_category: p.sub_category || "",
      price: String(p.price ?? ""), description: p.description || "", images: p.images || [],
      sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "), customizable: !!p.customizable,
      occasion: p.occasion || "",
      pattern_type: p.pattern_type || "", fabric_name: p.fabric_name || "", fabric_image: p.fabric_image || "",
      sleeve_type: p.sleeve_type || "", best_seller: !!p.best_seller,
    });
  };
  const cancelEditProduct = () => { setEditingProductId(null); setEditProductForm(BLANK_PRODUCT_FORM); };
  const saveEditProduct = async (id) => {
    try {
      await api.put(`/products/${id}`, {
        ...editProductForm,
        price: parseFloat(editProductForm.price || 0),
        sizes: editProductForm.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colors: editProductForm.colors.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Product updated");
      cancelEditProduct();
      loadCatProducts(activeParent, activeSubFilter);
    } catch (e) { toast.error("Update failed"); }
  };
  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast("Deleted"); loadCatProducts(activeParent, activeSubFilter);
  };
  const createProduct = async () => {
    if (!newProductForm.name || !newProductForm.price) { toast.error("Name and price are required"); return; }
    try {
      await api.post("/products", {
        ...newProductForm,
        price: parseFloat(newProductForm.price || 0),
        sizes: newProductForm.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colors: newProductForm.colors.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Product added");
      setNewProductForm({ ...BLANK_PRODUCT_FORM, category: activeParent, sub_category: activeSubFilter });
      loadCatProducts(activeParent, activeSubFilter);
    } catch (e) { toast.error("Create failed"); }
  };

  return (
    <div className="mt-6 space-y-8">
      <div className="border border-squire-jeans/25 bg-squire-jeans/[0.05] rounded-lg p-4 text-sm text-[#374151]" data-testid="categories-help">
        <strong>How this works:</strong> On the left are your <em>main</em> categories (Shirt, Pants, Suit…). <strong>Click a category</strong> to manage its <em>sub-categories</em> on the right — e.g. Shirt → Formal, Casual, Checked. Sub-categories show up as a filter bar on that category's page.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* LEFT — main categories */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="sq-eyebrow">Main Categories</div>
            <span className="text-[11px] text-[#9CA3AF]">click one to manage its sub-categories</span>
          </div>
          <div className="space-y-2">
            {topLevels.map((c) => (
              editingId === c.id ? (
                <div key={c.id} className="border border-black/20 rounded-lg p-4 bg-white" data-testid={`product-category-edit-${c.id}`}>
                  {[["key", "Key (e.g. shirts)"], ["label", "Title"], ["sort_order", "Sort Order"]].map(([k, l]) => (
                    <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
                      <input value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                    </label>
                  ))}
                  <div className="mt-3"><ImageUploader value={editForm.image} onChange={(img) => setEditForm({ ...editForm, image: img })} multiple={false} label="Category Image" /></div>
                  <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Custom Link (optional — e.g. /bespoke; blank = /shop/&lt;key&gt;)</span>
                    <input value={editForm.route || ""} onChange={(e) => setEditForm({ ...editForm, route: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                  </label>
                  <label className="flex items-center gap-2 mt-3">
                    <input data-testid={`product-category-menu-${c.id}`} type="checkbox" checked={editForm.in_products_menu !== false} onChange={(e) => setEditForm({ ...editForm, in_products_menu: e.target.checked })} />
                    <span className="text-xs">Show in header "Products" menu</span>
                  </label>
                  <label className="flex items-center gap-2 mt-2">
                    <input data-testid={`product-category-home-${c.id}`} type="checkbox" checked={!!editForm.in_home_houses} onChange={(e) => setEditForm({ ...editForm, in_home_houses: e.target.checked })} />
                    <span className="text-xs">Show in home "Four Houses" section</span>
                  </label>
                  <div className="flex gap-3 mt-4">
                    <button data-testid={`product-category-save-${c.id}`} onClick={() => saveEdit(c.id, true)} className="sq-btn-primary flex-1">Save Changes</button>
                    <button onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
                  </div>
                </div>
              ) : (
                <div key={c.id} data-testid={`product-category-${c.id}`} onClick={() => setActiveParent(c.key)}
                  className={`flex items-center gap-4 border rounded-lg p-3 cursor-pointer transition-all ${activeParent === c.key ? "border-squire-jeans bg-squire-jeans/[0.06] shadow-sm" : "border-black/10 hover:border-black/40 hover:bg-black/[0.02]"}`}>
                  <img src={c.image} alt="" className="w-14 h-14 object-contain bg-[#F5F5F5] rounded shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg truncate">{c.label}</div>
                    <div className="sq-eyebrow truncate">{c.key}{c.in_products_menu ? " · in menu" : " · hidden"}{c.in_home_houses ? " · four houses" : ""}</div>
                    <div className="text-[11px] text-squire-jeans mt-0.5 font-medium">{childCounts[c.key] || 0} sub-categories · manage →</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); startEdit(c); }} data-testid={`product-category-edit-${c.id}`} className="text-xs hover:underline">Edit</button>
                    <button onClick={(e) => { e.stopPropagation(); del(c.id, true); }} data-testid={`product-category-delete-${c.id}`} className="text-xs text-[#9CA3AF] hover:text-red-600">Delete</button>
                  </div>
                </div>
              )
            ))}
          </div>

          <details className="mt-4 border border-dashed border-black/20 rounded-lg p-4" data-testid="new-top-cat-panel">
            <summary className="cursor-pointer text-sm font-medium select-none">+ Add a new main category</summary>
            <div className="mt-3">
              {[["key", "Key (e.g. shirts)"], ["label", "Title"], ["sort_order", "Sort Order"]].map(([k, l]) => (
                <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
                  <input data-testid={`new-top-cat-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                </label>
              ))}
              <div className="mt-3"><ImageUploader value={form.image} onChange={(img) => setForm({ ...form, image: img })} multiple={false} label="Category Image" testid="new-top-cat-image" /></div>
              <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Custom Link (optional — e.g. /bespoke; blank = /shop/&lt;key&gt;)</span>
                <input data-testid="new-top-cat-route" value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
              </label>
              <label className="flex items-center gap-2 mt-3">
                <input data-testid="new-top-cat-menu" type="checkbox" checked={form.in_products_menu !== false} onChange={(e) => setForm({ ...form, in_products_menu: e.target.checked })} />
                <span className="text-xs">Show in header "Products" menu</span>
              </label>
              <label className="flex items-center gap-2 mt-2">
                <input data-testid="new-top-cat-home" type="checkbox" checked={!!form.in_home_houses} onChange={(e) => setForm({ ...form, in_home_houses: e.target.checked })} />
                <span className="text-xs">Show in home "Four Houses" section</span>
              </label>
              <button data-testid="new-top-cat-create" onClick={createTop} className="sq-btn-primary w-full mt-5">Add Category</button>
            </div>
          </details>
        </div>

        {/* RIGHT — sub-categories of the selected main category */}
        <div className="lg:sticky lg:top-24">
          {!activeParent ? (
            <div className="border-2 border-dashed border-black/15 rounded-lg p-10 text-center" data-testid="subcat-empty">
              <FolderTree size={28} className="mx-auto text-squire-jeans/60" />
              <div className="font-display text-lg mt-3">Manage sub-categories</div>
              <p className="text-sm text-[#4B5563] mt-1 max-w-xs mx-auto">Click a category on the left (e.g. Shirt) to add sub-categories like Formal, Casual or Checked.</p>
            </div>
          ) : (
            <div className="border border-black/10 rounded-lg overflow-hidden bg-white" data-testid="subcat-panel">
              <div className="px-5 py-4 bg-gradient-to-br from-squire-jeans to-black text-white">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">Sub-categories of</div>
                <div className="font-display text-2xl leading-none mt-1 capitalize">{(topLevels.find((t) => t.key === activeParent) || {}).label || activeParent}</div>
              </div>

              <div className="p-5">
                <div className="space-y-2">
                  {subs.length === 0 && <div className="text-sm text-[#4B5563] pb-1">No sub-categories yet — add the first one below.</div>}
                  {subs.map((c) => (
                    editingId === c.id ? (
                      <div key={c.id} className="border border-black/20 rounded-lg p-4" data-testid={`product-subcategory-edit-${c.id}`}>
                        {[["key", "Key (e.g. formal)"], ["label", "Title (e.g. Formal Shirt)"], ["sort_order", "Sort Order"]].map(([k, l]) => (
                          <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
                            <input value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                          </label>
                        ))}
                        <div className="mt-3"><ImageUploader value={editForm.image} onChange={(img) => setEditForm({ ...editForm, image: img })} multiple={false} label="Sub-Category Image" testid={`product-subcategory-edit-${c.id}-image`} /></div>
                        <p className="text-xs text-[#4B5563] mt-2">Link: {deriveSubCategoryRoute(activeParent, editForm.key)}</p>
                        <div className="flex gap-3 mt-4">
                          <button data-testid={`product-subcategory-save-${c.id}`} onClick={() => saveEdit(c.id, false)} className="sq-btn-primary flex-1">Save Changes</button>
                          <button onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div key={c.id} className="flex items-center gap-3 border border-black/10 rounded-lg p-3" data-testid={`product-subcategory-${c.id}`}>
                        {c.image ? <img src={c.image} alt="" className="w-11 h-11 object-contain bg-[#F5F5F5] rounded shrink-0" /> : <div className="w-11 h-11 rounded bg-[#F5F5F5] grid place-items-center text-[#9CA3AF] shrink-0"><FolderTree size={16} /></div>}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{c.label}</div>
                          <div className="sq-eyebrow truncate">{c.key}</div>
                        </div>
                        <button data-testid={`product-subcategory-edit-${c.id}`} onClick={() => startEdit(c)} className="text-xs hover:underline">Edit</button>
                        <button data-testid={`product-subcategory-delete-${c.id}`} onClick={() => del(c.id, false)} className="text-xs text-[#9CA3AF] hover:text-red-600">Delete</button>
                      </div>
                    )
                  ))}
                </div>

                <div className="mt-5 pt-4 border-t border-black/10">
                  <div className="sq-eyebrow mb-1">Add Sub-Category</div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className="sq-eyebrow text-[10px]">Key (e.g. formal)</span>
                      <input data-testid="new-sub-cat-key" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                    </label>
                    <label className="block"><span className="sq-eyebrow text-[10px]">Title (e.g. Formal Shirt)</span>
                      <input data-testid="new-sub-cat-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                    </label>
                  </div>
                  <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Sort Order</span>
                    <input data-testid="new-sub-cat-sort_order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                  </label>
                  <div className="mt-3"><ImageUploader value={form.image} onChange={(img) => setForm({ ...form, image: img })} multiple={false} label="Sub-Category Image (optional)" testid="new-sub-cat-image" /></div>
                  {form.key && <p className="text-xs text-[#4B5563] mt-2">Link will be: {deriveSubCategoryRoute(activeParent, form.key)}</p>}
                  <button data-testid="new-sub-cat-create" onClick={createSub} className="sq-btn-primary w-full mt-4">Add Sub-Category</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {activeParent && (
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div className="sq-eyebrow">Products in "{activeParent}"{activeSubFilter ? ` / ${activeSubFilter}` : ""}</div>
            {subs.length > 0 && (
              <select
                data-testid="category-product-subfilter"
                value={activeSubFilter}
                onChange={(e) => setActiveSubFilter(e.target.value)}
                className="bg-transparent border-b border-black/30 outline-none py-1 text-sm"
              >
                <option value="">All sub-categories</option>
                {subs.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-2">
              {catProducts.length === 0 && <div className="text-sm text-[#4B5563]">No products found here yet — add the first one from the panel on the right.</div>}
              {catProducts.map((p) => (
                editingProductId === p.id ? (
                  <div key={p.id} className="border border-black/10 p-4" data-testid={`category-product-edit-${p.id}`}>
                    <ProductForm value={editProductForm} onChange={setEditProductForm} onSubmit={() => saveEditProduct(p.id)} submitLabel="Save Changes" testidPrefix={`category-product-edit-${p.id}`} />
                    <button onClick={cancelEditProduct} className="w-full text-center text-xs text-[#4B5563] hover:text-black mt-3">Cancel</button>
                  </div>
                ) : (
                  <div key={p.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`category-product-${p.id}`}>
                    <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover" />
                    <div className="flex-1">
                      <div className="font-display text-lg">{p.name}</div>
                      <div className="sq-eyebrow">{p.id} · {p.sub_category} · {formatINR(p.price)}</div>
                    </div>
                    <button data-testid={`category-product-edit-${p.id}`} onClick={() => startEditProduct(p)} className="text-xs mr-3 hover:underline">Edit</button>
                    <button data-testid={`category-product-delete-${p.id}`} onClick={() => delProduct(p.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
                  </div>
                )
              ))}
            </div>
            <div className="border border-black/10 p-5 h-fit">
              <div className="sq-eyebrow">New Product in "{activeParent}"{activeSubFilter ? ` / ${activeSubFilter}` : ""}</div>
              <ProductForm value={newProductForm} onChange={setNewProductForm} onSubmit={createProduct} submitLabel="Add Product" testidPrefix="category-product-new" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Products (standalone tab): choose category → sub-category →
// add product. Same ProductForm used for create + inline edit.
// ============================================================
function ProductsAdmin() {
  const [categories, setCategories] = useState([]);
  const [subs, setSubs] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [filterSub, setFilterSub] = useState("");
  const [products, setProducts] = useState([]);
  const [newForm, setNewForm] = useState(BLANK_PRODUCT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_PRODUCT_FORM);

  const loadCats = () => api.get("/product-categories", { params: { top_level: true } }).then(({ data }) => setCategories((data || []).filter((c) => c.key !== "customise")));
  const loadProducts = () => {
    const params = { limit: 200 };
    if (filterCat) params.category = filterCat;
    if (filterSub) params.sub_category = filterSub;
    api.get("/products", { params }).then(({ data }) => setProducts(data.items || []));
  };
  useEffect(() => { loadCats(); }, []);
  useEffect(() => {
    if (filterCat) api.get("/product-categories", { params: { parent_key: filterCat } }).then(({ data }) => setSubs(data || []));
    else setSubs([]);
    setFilterSub("");
  }, [filterCat]);
  useEffect(() => { loadProducts(); }, [filterCat, filterSub]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = (f) => ({
    ...f,
    price: parseFloat(f.price || 0),
    sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
    colors: f.colors.split(",").map((s) => s.trim()).filter(Boolean),
  });
  const create = async () => {
    if (!newForm.category || !newForm.sub_category || !newForm.name || !newForm.price) {
      toast.error("Category, sub-category, name and price are required"); return;
    }
    try {
      await api.post("/products", payload(newForm));
      toast.success("Product added");
      setNewForm(BLANK_PRODUCT_FORM);
      loadProducts();
    } catch (e) { toast.error("Create failed"); }
  };
  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      name: p.name || "", category: p.category || "", sub_category: p.sub_category || "",
      price: String(p.price ?? ""), description: p.description || "", images: p.images || [],
      sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "),
      pattern_type: p.pattern_type || "", fabric_name: p.fabric_name || "", fabric_image: p.fabric_image || "",
      sleeve_type: p.sleeve_type || "", best_seller: !!p.best_seller, customizable: !!p.customizable, occasion: p.occasion || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_PRODUCT_FORM); };
  const saveEdit = async (id) => {
    try { await api.put(`/products/${id}`, payload(editForm)); toast.success("Product updated"); cancelEdit(); loadProducts(); }
    catch (e) { toast.error("Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast("Deleted"); loadProducts();
  };

  return (
    <div className="mt-6 space-y-8">
      <p className="text-sm text-[#4B5563] max-w-2xl">Add a product by choosing its category and sub-category (added under Product Categories), then filling in the details. Use the filters to browse and edit existing products.</p>

      <div className="flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="sq-eyebrow text-[10px]">Filter — Category</span>
          <select data-testid="products-filter-category" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="block bg-transparent border-b border-black/30 outline-none py-1 text-sm min-w-[180px]">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
        {subs.length > 0 && (
          <label className="block">
            <span className="sq-eyebrow text-[10px]">Filter — Sub-Category</span>
            <select data-testid="products-filter-sub" value={filterSub} onChange={(e) => setFilterSub(e.target.value)} className="block bg-transparent border-b border-black/30 outline-none py-1 text-sm min-w-[180px]">
              <option value="">All sub-categories</option>
              {subs.map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
            </select>
          </label>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-2">
          <div className="sq-eyebrow mb-2">Products ({products.length})</div>
          {products.length === 0 && <div className="text-sm text-[#4B5563]">No products yet — add the first from the panel on the right.</div>}
          {products.map((p) => (
            editingId === p.id ? (
              <div key={p.id} className="border border-black/10 p-4" data-testid={`products-edit-${p.id}`}>
                <ProductForm value={editForm} onChange={setEditForm} onSubmit={() => saveEdit(p.id)} submitLabel="Save Changes" testidPrefix={`products-edit-${p.id}`} />
                <button onClick={cancelEdit} className="w-full text-center text-xs text-[#4B5563] hover:text-black mt-3">Cancel</button>
              </div>
            ) : (
              <div key={p.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`products-row-${p.id}`}>
                <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover bg-[#F5F5F5]" />
                <div className="flex-1">
                  <div className="font-display text-lg">{p.name} {p.best_seller && <span className="text-[10px] uppercase tracking-[0.2em] bg-black text-white px-2 py-0.5 ml-1">Best</span>}</div>
                  <div className="sq-eyebrow">{p.id} · {p.category} / {p.sub_category} · {formatINR(p.price)}</div>
                </div>
                <button data-testid={`products-edit-btn-${p.id}`} onClick={() => startEdit(p)} className="text-xs mr-3 hover:underline">Edit</button>
                <button data-testid={`products-delete-${p.id}`} onClick={() => del(p.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
              </div>
            )
          ))}
        </div>
        <div className="border border-black/10 p-5 h-fit">
          <div className="sq-eyebrow">New Product</div>
          <ProductForm value={newForm} onChange={setNewForm} onSubmit={create} submitLabel="Add Product" testidPrefix="products-new" />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Fabrics (standalone tab). Name, up to 5 images, price per
// metre, fabric type, colours, best seller.
// ============================================================
const FABRIC_TYPE_OPTIONS = ["Cotton", "Linen", "Wool", "Silk", "Tweed", "Denim", "Corduroy", "Poly-Blend"];
const BLANK_FABRIC_FORM = { name: "", fabric_type: "", price_per_meter: "", images: [], colors: "", description: "", garments: [], best_seller: false, sort_order: 0 };

function FabricFields({ value, onChange, prefix, categories = [] }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const toggleGarment = (k) => {
    const cur = value.garments || [];
    set({ garments: cur.includes(k) ? cur.filter((g) => g !== k) : [...cur, k] });
  };
  return (
    <>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Fabric Name *</span>
        <input data-testid={`${prefix}-name`} value={value.name} onChange={(e) => set({ name: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Fabric Type</span>
        <input list={`${prefix}-typelist`} data-testid={`${prefix}-fabric_type`} value={value.fabric_type} onChange={(e) => set({ fabric_type: e.target.value })} placeholder="e.g. Cotton, Linen" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        <datalist id={`${prefix}-typelist`}>{FABRIC_TYPE_OPTIONS.map((t) => <option key={t} value={t} />)}</datalist>
      </label>

      {/* Used for — which categories this fabric can be tailored into.
          Multi-select: the same fabric can serve several garments. */}
      <div className="mt-4">
        <span className="sq-eyebrow text-[10px]">Used For (select all that apply)</span>
        <div className="grid grid-cols-2 gap-2 mt-2" data-testid={`${prefix}-garments`}>
          {categories.map((c) => {
            const on = (value.garments || []).includes(c.key);
            return (
              <label key={c.key} data-testid={`${prefix}-garment-${c.key}`} className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm transition-colors ${on ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>
                <input type="checkbox" checked={on} onChange={() => toggleGarment(c.key)} className="accent-squire-jeans w-4 h-4" />
                {c.label}
              </label>
            );
          })}
        </div>
        {categories.length === 0 && <p className="text-[10px] text-[#9CA3AF] mt-1">Add categories under Bespoke Categories first.</p>}
        <p className="text-[10px] text-[#9CA3AF] mt-1">Leave all unchecked to offer this fabric for every category.</p>
      </div>

      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Price per Metre (₹) *</span>
        <input data-testid={`${prefix}-price_per_meter`} type="number" value={value.price_per_meter} onChange={(e) => set({ price_per_meter: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Colour (comma for multiple)</span>
        <input data-testid={`${prefix}-colors`} value={value.colors} onChange={(e) => set({ colors: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Description (optional)</span>
        <input data-testid={`${prefix}-description`} value={value.description} onChange={(e) => set({ description: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <div className="mt-3">
        <ImageUploader value={value.images} onChange={(imgs) => set({ images: imgs })} multiple={true} max={5} label="Fabric Images (up to 5 — 2nd shown on hover)" testid={`${prefix}-images`} />
      </div>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Sort Order</span>
        <input data-testid={`${prefix}-sort_order`} type="number" value={value.sort_order} onChange={(e) => set({ sort_order: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="flex items-center gap-2 mt-3">
        <input data-testid={`${prefix}-best_seller`} type="checkbox" checked={!!value.best_seller} onChange={(e) => set({ best_seller: e.target.checked })} />
        <span className="text-xs">Mark as Best Seller</span>
      </label>
    </>
  );
}

function FabricsAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(BLANK_FABRIC_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_FABRIC_FORM);

  const load = () => api.get("/fabrics").then(({ data }) => setItems(data || []));
  useEffect(() => {
    load();
    api.get("/bespoke-categories").then(({ data }) => setCategories((data || []).map((c) => ({ key: c.key, label: c.label })))).catch(() => setCategories([]));
  }, []);

  const payload = (f) => ({
    name: f.name,
    fabric_type: f.fabric_type,
    price_per_meter: parseFloat(f.price_per_meter || 0),
    images: f.images,
    description: f.description,
    colors: (f.colors || "").split(",").map((s) => s.trim()).filter(Boolean),
    garments: f.garments || [],
    best_seller: !!f.best_seller,
    sort_order: Number(f.sort_order) || 0,
  });
  const create = async () => {
    if (!form.name || !form.price_per_meter) { toast.error("Name and price per metre are required"); return; }
    try { await api.post("/fabrics", payload(form)); toast.success("Fabric added"); setForm(BLANK_FABRIC_FORM); load(); }
    catch (e) { toast.error("Create failed"); }
  };
  const startEdit = (f) => {
    setEditingId(f.id);
    setEditForm({
      name: f.name || "", fabric_type: f.fabric_type || "", price_per_meter: String(f.price_per_meter ?? ""),
      images: f.images || [], colors: (f.colors || []).join(", "), description: f.description || "",
      garments: f.garments || [],
      best_seller: !!f.best_seller, sort_order: f.sort_order || 0,
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_FABRIC_FORM); };
  const saveEdit = async (id) => {
    try { await api.put(`/fabrics/${id}`, payload(editForm)); toast.success("Fabric updated"); cancelEdit(); load(); }
    catch (e) { toast.error("Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this fabric?")) return;
    await api.delete(`/fabrics/${id}`); toast("Deleted"); load();
  };

  return (
    <div className="mt-6 space-y-8">
      <p className="text-sm text-[#4B5563] max-w-2xl">Fabrics shown under the header "Fabrics" menu. Each is priced per metre; the storefront auto-calculates totals for 1 / 1.25 / 1.5 / 1.75 / 2 metres. The second image is shown on hover.</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-2">
          <div className="sq-eyebrow mb-2">Fabrics ({items.length})</div>
          {items.length === 0 && <div className="text-sm text-[#4B5563]">No fabrics yet — add the first from the panel on the right.</div>}
          {items.map((f) => (
            editingId === f.id ? (
              <div key={f.id} className="border border-black/10 p-4" data-testid={`fabric-edit-${f.id}`}>
                <FabricFields value={editForm} onChange={setEditForm} prefix={`fabric-edit-${f.id}`} categories={categories} />
                <div className="flex gap-3 mt-4">
                  <button data-testid={`fabric-save-${f.id}`} onClick={() => saveEdit(f.id)} className="sq-btn-primary flex-1">Save Changes</button>
                  <button onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
                </div>
              </div>
            ) : (
              <div key={f.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`fabric-row-${f.id}`}>
                <img src={f.images?.[0]} alt="" className="w-14 h-14 object-cover bg-[#F5F5F5]" />
                <div className="flex-1">
                  <div className="font-display text-lg">{f.name} {f.best_seller && <span className="text-[10px] uppercase tracking-[0.2em] bg-black text-white px-2 py-0.5 ml-1">Best</span>}</div>
                  <div className="sq-eyebrow">{f.id} · {f.fabric_type || "—"} · {formatINR(f.price_per_meter)}/m{f.garments?.length ? ` · ${f.garments.join(", ")}` : " · all garments"}</div>
                </div>
                <button data-testid={`fabric-edit-btn-${f.id}`} onClick={() => startEdit(f)} className="text-xs mr-3 hover:underline">Edit</button>
                <button data-testid={`fabric-delete-${f.id}`} onClick={() => del(f.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
              </div>
            )
          ))}
        </div>
        <div className="border border-black/10 p-5 h-fit">
          <div className="sq-eyebrow">New Fabric</div>
          <FabricFields value={form} onChange={setForm} prefix="fabric-new" categories={categories} />
          <button data-testid="fabric-new-create" onClick={create} className="sq-btn-primary w-full mt-5">Add Fabric</button>
        </div>
      </div>
    </div>
  );
}

const BLANK_TESTIMONIAL_FORM = { name: "", city: "", quote: "", image: "", sort_order: 0 };

function HomeVideosAdmin() {
  const [form, setForm] = useState({ hero_video: "", banner_video: "" });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/home-videos").then(({ data }) => { setForm({ hero_video: data.hero_video || "", banner_video: data.banner_video || "" }); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const ytId = (u) => (String(u || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/) || [])[1];
  const save = async () => {
    try { await api.put("/home-videos", form); toast.success("Home videos saved"); }
    catch (e) { toast.error("Save failed"); }
  };
  const fields = [
    { k: "hero_video", label: "Hero Video (top of home page)", help: "The big muted, looping video visitors see first." },
    { k: "banner_video", label: "Second Video (full-width banner)", help: "The full-screen video banner lower on the home page." },
  ];
  return (
    <div className="max-w-3xl">
      <p className="text-sm text-[#4B5563] mb-5 max-w-2xl">Paste any YouTube link (watch, share, or embed). The <strong>hero video</strong> plays full-screen at the very top of the home page; the <strong>second video</strong> is the full-width banner further down. Leave a field blank to keep the built-in default.</p>
      <div className="space-y-6">
        {fields.map(({ k, label, help }) => {
          const id = ytId(form[k]);
          return (
            <div key={k} className="border border-black/10 rounded-lg p-5">
              <div className="sq-eyebrow text-squire-jeans">{label}</div>
              <p className="text-xs text-[#4B5563] mt-1 mb-3">{help}</p>
              <input data-testid={`home-video-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className="w-full bg-transparent border-b border-black/30 outline-none py-1.5 text-sm" />
              {id ? (
                <div className="aspect-video mt-4 bg-black rounded overflow-hidden max-w-md">
                  <iframe src={`https://www.youtube.com/embed/${id}`} title={label} allow="encrypted-media" frameBorder="0" className="w-full h-full" />
                </div>
              ) : form[k] ? <p className="text-[11px] text-amber-700 mt-2">Not a recognised YouTube URL — it'll be treated as a direct video file.</p> : null}
            </div>
          );
        })}
      </div>
      <button data-testid="home-video-save" onClick={save} disabled={!loaded} className="sq-btn-primary mt-6">Save Videos</button>
    </div>
  );
}

function TestimonialFields({ value, onChange, prefix }) {
  return (
    <>
      {[["name", "Name"], ["role", "Role / Title (e.g. Founder, Woodside Inn)"], ["quote", "Quote"], ["sort_order", "Sort Order"]].map(([k, l]) => (
        <label key={k} className="block mt-3">
          <span className="sq-eyebrow text-[10px]">{l}</span>
          <input data-testid={`${prefix}-${k}`} value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </label>
      ))}
      <div className="mt-3">
        <ImageUploader value={value.image} onChange={(img) => onChange({ ...value, image: img })} multiple={false} label="Photo" testid={`${prefix}-image`} />
      </div>
    </>
  );
}

function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK_TESTIMONIAL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_TESTIMONIAL_FORM);

  const load = () => api.get("/testimonials").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.quote || !form.image) { toast.error("Name, quote and image are required"); return; }
    try {
      await api.post("/testimonials", { ...form, sort_order: Number(form.sort_order) || 0 });
      toast.success("Testimonial added");
      setForm(BLANK_TESTIMONIAL_FORM);
      load();
    } catch (e) { toast.error("Create failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/testimonials/${id}`); toast("Deleted"); load();
  };
  const startEdit = (t) => { setEditingId(t.id); setEditForm({ ...BLANK_TESTIMONIAL_FORM, ...t }); };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_TESTIMONIAL_FORM); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/testimonials/${id}`, { ...editForm, sort_order: Number(editForm.sort_order) || 0 });
      toast.success("Testimonial updated");
      cancelEdit();
      load();
    } catch (e) { toast.error("Update failed"); }
  };


  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-2">
        {items.length === 0 && <div className="text-sm text-[#4B5563]">No testimonials yet.</div>}
        {items.map((t) => (
          editingId === t.id ? (
            <div key={t.id} className="border border-black/10 p-4" data-testid={`testimonial-edit-${t.id}`}>
              <TestimonialFields value={editForm} onChange={setEditForm} prefix={`testimonial-edit-${t.id}`} />
              <div className="flex gap-3 mt-4">
                <button data-testid={`testimonial-save-${t.id}`} onClick={() => saveEdit(t.id)} className="sq-btn-primary flex-1">Save Changes</button>
                <button onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
              </div>
            </div>
          ) : (
            <div key={t.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`testimonial-${t.id}`}>
              <img src={t.image} alt="" className="w-14 h-14 object-cover" />
              <div className="flex-1">
                <div className="font-display text-base">"{t.quote}"</div>
                <div className="sq-eyebrow mt-1">{t.name} · {t.city}</div>
              </div>
              <button data-testid={`testimonial-edit-${t.id}`} onClick={() => startEdit(t)} className="text-xs mr-3 hover:underline">Edit</button>
              <button data-testid={`testimonial-delete-${t.id}`} onClick={() => del(t.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
            </div>
          )
        ))}
      </div>
      <div className="border border-black/10 p-5 h-fit">
        <div className="sq-eyebrow">New Testimonial</div>
        <TestimonialFields value={form} onChange={setForm} prefix="testimonial-new" />
        <button data-testid="testimonial-create" onClick={create} className="sq-btn-primary w-full mt-5">Add Testimonial</button>
      </div>
    </div>
  );
}

const BLANK_REEL_FORM = { title: "", image: "", video_url: "", sort_order: 0 };

function ReelFields({ value, onChange, prefix }) {
  return (
    <>
      {[["title", "Title"], ["video_url", "Video URL (YouTube or .mp4)"], ["sort_order", "Sort Order"]].map(([k, l]) => (
        <label key={k} className="block mt-3">
          <span className="sq-eyebrow text-[10px]">{l}</span>
          <input data-testid={`${prefix}-${k}`} value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </label>
      ))}
      <div className="mt-3">
        <ImageUploader value={value.image} onChange={(img) => onChange({ ...value, image: img })} multiple={false} label="Cover Image" testid={`${prefix}-image`} />
      </div>
    </>
  );
}

function ReelsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK_REEL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_REEL_FORM);

  const load = () => api.get("/reels").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.image) { toast.error("Title and cover image are required"); return; }
    try {
      await api.post("/reels", { ...form, sort_order: Number(form.sort_order) || 0 });
      toast.success("Reel added");
      setForm(BLANK_REEL_FORM);
      load();
    } catch (e) { toast.error("Create failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this reel?")) return;
    await api.delete(`/reels/${id}`); toast("Deleted"); load();
  };
  const startEdit = (r) => { setEditingId(r.id); setEditForm({ ...BLANK_REEL_FORM, ...r }); };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_REEL_FORM); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/reels/${id}`, { ...editForm, sort_order: Number(editForm.sort_order) || 0 });
      toast.success("Reel updated");
      cancelEdit();
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-2">
        {items.length === 0 && <div className="text-sm text-[#4B5563]">No reels yet.</div>}
        {items.map((r) => (
          editingId === r.id ? (
            <div key={r.id} className="border border-black/10 p-4" data-testid={`reel-edit-${r.id}`}>
              <ReelFields value={editForm} onChange={setEditForm} prefix={`reel-edit-${r.id}`} />
              <div className="flex gap-3 mt-4">
                <button data-testid={`reel-save-${r.id}`} onClick={() => saveEdit(r.id)} className="sq-btn-primary flex-1">Save Changes</button>
                <button onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
              </div>
            </div>
          ) : (
            <div key={r.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`reel-${r.id}`}>
              <img src={r.image} alt="" className="w-14 h-20 object-cover" />
              <div className="flex-1">
                <div className="font-display text-lg">{r.title}</div>
                <div className="sq-eyebrow truncate max-w-xs">{r.video_url}</div>
              </div>
              <button data-testid={`reel-edit-${r.id}`} onClick={() => startEdit(r)} className="text-xs mr-3 hover:underline">Edit</button>
              <button data-testid={`reel-delete-${r.id}`} onClick={() => del(r.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
            </div>
          )
        ))}
      </div>
      <div className="border border-black/10 p-5 h-fit">
        <div className="sq-eyebrow">New Reel</div>
        <ReelFields value={form} onChange={setForm} prefix="reel-new" />
        <button data-testid="reel-create" onClick={create} className="sq-btn-primary w-full mt-5">Add Reel</button>
      </div>
    </div>
  );
}

const BLANK_SEASONAL_TILE_FORM = { name: "", slug: "", image: "", hover: "", route: "", sort_order: 0 };

function SeasonalTileFields({ value, onChange, prefix }) {
  return (
    <>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Name</span>
        <input data-testid={`${prefix}-name`} value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      <label className="block mt-3">
        <span className="sq-eyebrow text-[10px]">Occasion Key (links to products' "Occasion" field — leave blank to auto-generate)</span>
        <input data-testid={`${prefix}-slug`} value={value.slug} onChange={(e) => onChange({ ...value, slug: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
      </label>
      {[["route", "Link (optional, overrides the occasion page)"], ["sort_order", "Sort Order"]].map(([k, l]) => (
        <label key={k} className="block mt-3">
          <span className="sq-eyebrow text-[10px]">{l}</span>
          <input data-testid={`${prefix}-${k}`} value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </label>
      ))}
      <div className="mt-3">
        <ImageUploader value={value.image} onChange={(img) => onChange({ ...value, image: img })} multiple={false} label="Image" testid={`${prefix}-image`} />
      </div>
      <div className="mt-3">
        <ImageUploader value={value.hover} onChange={(img) => onChange({ ...value, hover: img })} multiple={false} label="Hover Image (optional)" testid={`${prefix}-hover`} />
      </div>
    </>
  );
}

function SeasonalTilesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK_SEASONAL_TILE_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_SEASONAL_TILE_FORM);

  const [activeSlug, setActiveSlug] = useState("");
  const [seasonProducts, setSeasonProducts] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editProductForm, setEditProductForm] = useState(BLANK_PRODUCT_FORM);

  const load = () => api.get("/seasonal-tiles").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const loadSeasonProducts = (slug) => {
    if (!slug) { setSeasonProducts([]); return; }
    api.get("/products", { params: { occasion: slug, limit: 200 } }).then(({ data }) => setSeasonProducts(data.items || []));
  };
  useEffect(() => { loadSeasonProducts(activeSlug); }, [activeSlug]);

  const create = async () => {
    if (!form.name || !form.image) { toast.error("Name and image are required"); return; }
    const slug = form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    try {
      await api.post("/seasonal-tiles", { ...form, slug, sort_order: Number(form.sort_order) || 0 });
      toast.success("Tile added");
      setForm(BLANK_SEASONAL_TILE_FORM);
      load();
    } catch (e) { toast.error("Create failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this tile?")) return;
    await api.delete(`/seasonal-tiles/${id}`); toast("Deleted"); load();
  };
  const startEdit = (t) => { setEditingId(t.id); setEditForm({ ...BLANK_SEASONAL_TILE_FORM, ...t }); };
  const cancelEdit = () => { setEditingId(null); setEditForm(BLANK_SEASONAL_TILE_FORM); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/seasonal-tiles/${id}`, { ...editForm, sort_order: Number(editForm.sort_order) || 0 });
      toast.success("Tile updated");
      cancelEdit();
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const startEditProduct = (p) => {
    setEditingProductId(p.id);
    setEditProductForm({
      name: p.name || "", category: p.category || "", sub_category: p.sub_category || "",
      price: String(p.price ?? ""), description: p.description || "", images: p.images || [],
      sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "), customizable: !!p.customizable,
      occasion: p.occasion || activeSlug,
      pattern_type: p.pattern_type || "", fabric_name: p.fabric_name || "", fabric_image: p.fabric_image || "",
      sleeve_type: p.sleeve_type || "", best_seller: !!p.best_seller,
    });
  };
  const cancelEditProduct = () => { setEditingProductId(null); setEditProductForm(BLANK_PRODUCT_FORM); };
  const saveEditProduct = async (id) => {
    try {
      await api.put(`/products/${id}`, {
        ...editProductForm,
        price: parseFloat(editProductForm.price || 0),
        sizes: editProductForm.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colors: editProductForm.colors.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Product updated");
      cancelEditProduct();
      loadSeasonProducts(activeSlug);
    } catch (e) { toast.error("Update failed"); }
  };
  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast("Deleted"); loadSeasonProducts(activeSlug);
  };

  return (
    <div className="mt-6 space-y-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-2">
          {items.length === 0 && <div className="text-sm text-[#4B5563]">No seasonal tiles yet.</div>}
          {items.map((t) => (
            editingId === t.id ? (
              <div key={t.id} className="border border-black/10 p-4" data-testid={`seasonal-tile-edit-${t.id}`}>
                <SeasonalTileFields value={editForm} onChange={setEditForm} prefix={`seasonal-tile-edit-${t.id}`} />
                <div className="flex gap-3 mt-4">
                  <button data-testid={`seasonal-tile-save-${t.id}`} onClick={() => saveEdit(t.id)} className="sq-btn-primary flex-1">Save Changes</button>
                  <button onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
                </div>
              </div>
            ) : (
              <button
                key={t.id}
                onClick={() => setActiveSlug(t.slug)}
                data-testid={`seasonal-tile-${t.id}`}
                className={`w-full flex items-center gap-4 border p-3 text-left ${activeSlug === t.slug ? "border-black" : "border-black/10"}`}
              >
                <img src={t.image} alt="" className="w-14 h-14 object-cover" />
                <div className="flex-1">
                  <div className="font-display text-lg">{t.name}</div>
                  <div className="sq-eyebrow">occasion: {t.slug}{t.route ? ` · ${t.route}` : ""}</div>
                </div>
                <span onClick={(e) => { e.stopPropagation(); startEdit(t); }} data-testid={`seasonal-tile-edit-${t.id}`} className="text-xs mr-3 hover:underline">Edit</span>
                <span onClick={(e) => { e.stopPropagation(); del(t.id); }} data-testid={`seasonal-tile-delete-${t.id}`} className="text-xs text-[#4B5563] hover:text-black">Delete</span>
              </button>
            )
          ))}
        </div>
        <div className="border border-black/10 p-5 h-fit">
          <div className="sq-eyebrow">New Seasonal Category</div>
          <SeasonalTileFields value={form} onChange={setForm} prefix="seasonal-tile-new" />
          <button data-testid="seasonal-tile-create" onClick={create} className="sq-btn-primary w-full mt-5">Add Tile</button>
        </div>
      </div>

      {activeSlug && (
        <div>
          <div className="sq-eyebrow mb-3">Products tagged "{activeSlug}"</div>
          <p className="text-xs text-[#4B5563] mb-4 max-w-2xl">These are products whose "Occasion" field matches this tile's occasion key. Set that field from the Products tab, or edit a product below.</p>
          <div className="space-y-2">
            {seasonProducts.length === 0 && <div className="text-sm text-[#4B5563]">No products tagged with "{activeSlug}" yet.</div>}
            {seasonProducts.map((p) => (
              editingProductId === p.id ? (
                <div key={p.id} className="border border-black/10 p-4" data-testid={`season-product-edit-${p.id}`}>
                  <ProductForm value={editProductForm} onChange={setEditProductForm} onSubmit={() => saveEditProduct(p.id)} submitLabel="Save Changes" testidPrefix={`season-product-edit-${p.id}`} />
                  <button onClick={cancelEditProduct} className="w-full text-center text-xs text-[#4B5563] hover:text-black mt-3">Cancel</button>
                </div>
              ) : (
                <div key={p.id} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`season-product-${p.id}`}>
                  <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover" />
                  <div className="flex-1">
                    <div className="font-display text-lg">{p.name}</div>
                    <div className="sq-eyebrow">{p.id} · {p.category} · {formatINR(p.price)}</div>
                  </div>
                  <button data-testid={`season-product-edit-${p.id}`} onClick={() => startEditProduct(p)} className="text-xs mr-3 hover:underline">Edit</button>
                  <button data-testid={`season-product-delete-${p.id}`} onClick={() => delProduct(p.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CouponAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", discount_pct: 10, scope: "all", valid_days: 30 });
  const load = () => api.get("/coupons").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/coupons", { ...form, discount_pct: Number(form.discount_pct), valid_days: Number(form.valid_days) }); toast("Coupon created"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
      <div className="lg:col-span-2 space-y-2">
        {items.map((c) => (
          <div key={c.id} className="border border-black/10 p-3 flex justify-between text-sm" data-testid={`coupon-${c.code}`}>
            <span className="font-display text-lg">{c.code}</span>
            <span>{c.discount_pct}% off · {c.scope}</span>
            <span className="text-[#4B5563]">{c.id}</span>
          </div>
        ))}
      </div>
      <div className="border border-black/10 p-5">
        <div className="sq-eyebrow">New Coupon</div>
        {[["code", "CODE"], ["discount_pct", "% Discount"], ["valid_days", "Valid days"]].map(([k, l]) => (
          <label key={k} className="block mt-3">
            <span className="sq-eyebrow text-[10px]">{l}</span>
            <input data-testid={`coupon-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
        ))}
        <label className="block mt-3">
          <span className="sq-eyebrow text-[10px]">Scope</span>
          <select data-testid="coupon-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            {["all", "fabric", "bespoke", "accessory"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <button data-testid="coupon-create" onClick={create} className="sq-btn-primary w-full mt-5">Generate Coupon</button>
      </div>
    </div>
  );
}

function ScheduleAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ section: "lookbook", slot_key: "tile1", title: "", subtitle: "", image_url: "", secondary_image_url: "", link: "", starts_at: new Date().toISOString().slice(0, 10), ends_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), why_to_wear: "" });
  const load = () => api.get("/content/active").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/content/schedule", { ...form, starts_at: new Date(form.starts_at).toISOString(), ends_at: new Date(form.ends_at).toISOString() }); toast("Scheduled"); load(); }
    catch (e) { toast.error("Failed (need major_admin or super_admin)"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
      <div className="lg:col-span-2 space-y-2 max-h-[600px] overflow-y-auto sq-scroll">
        {items.map((c, i) => (
          <div key={i} className="border border-black/10 p-3 flex items-center gap-3 text-sm">
            {c.image_url && <img src={c.image_url} alt="" className="w-14 h-14 object-cover" />}
            <div className="flex-1">
              <div className="font-display">{c.title || c.slot_key}</div>
              <div className="sq-eyebrow">{c.section} · {c.starts_at?.slice(0, 10)} → {c.ends_at?.slice(0, 10)}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="border border-black/10 p-5">
        <div className="sq-eyebrow">Schedule Content</div>
        <label className="block mt-3">
          <span className="sq-eyebrow text-[10px]">Section</span>
          <select data-testid="sched-section" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            {["category_frames", "lookbook", "seasonal_spice", "horizontal_banner", "trending_wear", "scroll_banner"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        {[["slot_key", "Slot Key"], ["title", "Title"], ["subtitle", "Subtitle"], ["link", "Link"], ["why_to_wear", "Why to wear (trending wear only)"], ["starts_at", "Starts (yyyy-mm-dd)"], ["ends_at", "Ends (yyyy-mm-dd)"]].map(([k, l]) => (
          <label key={k} className="block mt-3">
            <span className="sq-eyebrow text-[10px]">{l}</span>
            <input data-testid={`sched-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
        ))}
        <div className="mt-3">
          <ImageUploader value={form.image_url} onChange={(u) => setForm({ ...form, image_url: u })} label="Image" testid="sched-image" />
        </div>
        <div className="mt-3">
          <ImageUploader value={form.secondary_image_url} onChange={(u) => setForm({ ...form, secondary_image_url: u })} label="Hover Image (optional)" testid="sched-hover" />
        </div>
        <button data-testid="sched-create" onClick={create} className="sq-btn-primary w-full mt-5">Schedule</button>
      </div>
    </div>
  );
}


function ReturnsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/returns").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const act = async (rid, status) => { try { await api.post(`/admin/returns/${rid}/action`, { status }); toast(`Marked ${status}`); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="space-y-3">
      {items.length === 0 && <div className="text-sm text-[#4B5563]">No return / alteration requests yet.</div>}
      {items.map((r) => (
        <div key={r.id} className="border border-black/10 p-4" data-testid={`ret-admin-${r.id}`}>
          <div className="flex flex-wrap justify-between gap-2">
            <div>
              <div className="font-display text-lg">{r.id} · <span className="sq-eyebrow">{r.type.toUpperCase()}</span></div>
              <div className="sq-eyebrow text-[10px] mt-1">{r.order_id} · {r.user_id} · {r.created_at?.slice(0, 10)}</div>
            </div>
            <div className="sq-eyebrow"><span className="px-2 py-1 bg-squire-jeans text-white">{r.status}</span></div>
          </div>
          <div className="text-sm mt-2"><strong>{r.reason}</strong> — {r.issue_detail}</div>
          {r.photo_urls?.length > 0 && <div className="flex gap-2 mt-2">{r.photo_urls.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 object-cover border border-black/10" />)}</div>}
          {r.refund_id && <div className="mt-2 text-xs text-squire-jeans">Refund issued: {r.refund_id}</div>}
          <div className="mt-3 flex gap-2">
            <button data-testid={`ret-approve-${r.id}`} onClick={() => act(r.id, "approved")} className="text-xs border border-black/30 px-3 py-1 hover:border-squire-jeans">Approve</button>
            <button data-testid={`ret-pickedup-${r.id}`} onClick={() => act(r.id, "picked_up")} className="text-xs border border-black/30 px-3 py-1 hover:border-squire-jeans">Picked Up</button>
            <button data-testid={`ret-refunded-${r.id}`} onClick={() => act(r.id, "refunded")} className="text-xs bg-squire-jeans text-white px-3 py-1">Refund</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function BundlesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", min_subtotal: "10000", discount_pct: "0", free_product_id: "", description: "", active: true });
  const load = () => api.get("/bundles").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/bundles", { ...form, min_subtotal: Number(form.min_subtotal), discount_pct: Number(form.discount_pct) }); toast("Bundle saved"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-2">
        {items.map((b) => (
          <div key={b.id} className="border border-black/10 p-4" data-testid={`bundle-${b.id}`}>
            <div className="flex justify-between"><div className="font-display text-lg">{b.name}</div><div className="sq-eyebrow">{b.id}</div></div>
            <div className="text-sm text-[#4B5563]">{b.description}</div>
            <div className="sq-eyebrow text-[10px] mt-1">min ₹{b.min_subtotal} · {b.discount_pct}% · free product {b.free_product_id || "—"}</div>
          </div>
        ))}
      </div>
      <div className="border border-black/10 p-5 h-fit">
        <div className="sq-eyebrow">New Bundle</div>
        {[["name", "Name"], ["min_subtotal", "Min Subtotal ₹"], ["discount_pct", "Discount %"], ["free_product_id", "Free Product ID"], ["description", "Description"]].map(([k, l]) => (
          <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
            <input data-testid={`bundle-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
        ))}
        <button data-testid="bundle-create" onClick={create} className="sq-btn-primary w-full mt-5">Create</button>
      </div>
    </div>
  );
}

function StatisticsAdmin() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/admin/statistics", { params: { days } }).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  const Card = ({ label, value, sub }) => (
    <div className="border border-black/10 rounded-xl p-5">
      <div className="sq-eyebrow text-[#4B5563]">{label}</div>
      <div className="font-display text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-[#4B5563] mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="sq-eyebrow">Insights</div>
          <h2 className="font-display text-3xl mt-1">Statistics</h2>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="stats-range" className="border border-black/20 rounded px-3 py-2 text-sm outline-none">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 12 months</option>
          <option value={0}>All time</option>
        </select>
      </div>

      {loading || !data ? <div className="text-sm text-[#4B5563]">Loading…</div> : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Readymade Orders" value={data.totals.orders} />
            <Card label="Bespoke Orders" value={data.totals.bespoke_orders} />
            <Card label="Total Revenue" value={formatINR(data.totals.revenue)} />
            <Card label="Busiest 2-hr Slot" value={data.busiest_slot.slot || "—"} sub={`${data.busiest_slot.orders} orders`} />
          </div>

          {/* #16 — orders by 2-hour slot */}
          <div className="border border-black/10 rounded-xl p-5">
            <div className="font-display text-xl mb-1">Orders by 2-hour slot</div>
            <p className="text-xs text-[#4B5563] mb-4">Highest-volume window: <strong>{data.busiest_slot.slot || "—"}</strong></p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.slots}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="slot" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="orders" fill="#3b4a9e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* #16 — sales by weekday */}
          <div className="border border-black/10 rounded-xl p-5">
            <div className="font-display text-xl mb-1">Sales by day of week</div>
            <p className="text-xs text-[#4B5563] mb-4">Highest-sales day: <strong>{data.top_day.day || "—"}</strong> ({formatINR(data.top_day.revenue)})</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.weekday}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Bar dataKey="revenue" fill="#f7d33f" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* #19 — product-type breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-black/10 rounded-xl p-5">
              <div className="font-display text-xl mb-3">Readymade by category</div>
              {data.readymade.length === 0 ? <div className="text-sm text-[#4B5563]">No data.</div> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-[#4B5563] text-xs uppercase tracking-wider"><th className="text-left py-1">Category</th><th className="text-right">Units</th><th className="text-right">Revenue</th></tr></thead>
                  <tbody>
                    {data.readymade.map((r) => (
                      <tr key={r.category} className="border-t border-black/5"><td className="py-1.5 capitalize">{r.category}</td><td className="text-right">{r.count}</td><td className="text-right">{formatINR(r.revenue)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="border border-black/10 rounded-xl p-5">
              <div className="font-display text-xl mb-3">Bespoke by garment</div>
              {data.bespoke.length === 0 ? <div className="text-sm text-[#4B5563]">No data.</div> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-[#4B5563] text-xs uppercase tracking-wider"><th className="text-left py-1">Garment</th><th className="text-right">Orders</th><th className="text-right">Revenue</th></tr></thead>
                  <tbody>
                    {data.bespoke.map((r) => (
                      <tr key={r.garment} className="border-t border-black/5"><td className="py-1.5 capitalize">{r.garment}</td><td className="text-right">{r.count}</td><td className="text-right">{formatINR(r.revenue)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* #15 — most-ordered cities */}
          <div className="border border-black/10 rounded-xl p-5">
            <div className="font-display text-xl mb-3">Most-ordered cities</div>
            {data.top_cities.length === 0 ? <div className="text-sm text-[#4B5563]">No city data yet.</div> : (
              <div className="flex flex-wrap gap-2">
                {data.top_cities.map((c, i) => (
                  <span key={c.city} className="inline-flex items-center gap-2 border border-black/15 rounded-full px-3 py-1.5 text-sm">
                    <span className="text-[#9CA3AF]">#{i + 1}</span> {c.city} <span className="font-semibold">{c.orders}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const ORDER_SEGMENTS = [
  { key: "readymade", label: "Ready Made" },
  { key: "bespoke", label: "Bespoke" },
  { key: "fabric", label: "Fabric" },
  { key: "accessory", label: "Accessories" },
];

function OrderSegmentsAdmin() {
  const [segment, setSegment] = useState("readymade");
  const [view, setView] = useState("all");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const limit = 10;

  const load = () => {
    setLoading(true);
    api.get("/admin/order-segments", { params: { segment, view, status, page, limit } })
      .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [segment, view, status, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [segment, view, status]);
  useEffect(() => { setStatus(""); setView("all"); }, [segment]);

  const counts = data?.counts || { all: 0, pending: 0, missed: 0 };

  return (
    <div data-testid="order-segments">
      <div className="sq-eyebrow mb-1">Orders by Type</div>
      <p className="text-sm text-[#4B5563] mb-4">Drill into orders by type. <strong>Missed</strong> = an active order whose status hasn't changed in over 5 days.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {ORDER_SEGMENTS.map((s) => (
          <button key={s.key} data-testid={`orderseg-${s.key}`} onClick={() => setSegment(s.key)}
            className={`px-4 py-2 text-sm border ${segment === s.key ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>{s.label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        {[["all", "All"], ["pending", "Pending"], ["missed", "Missed"]].map(([k, l]) => (
          <button key={k} data-testid={`orderview-${k}`} onClick={() => setView(k)}
            className={`px-3 py-1.5 text-sm rounded-full border ${view === k ? "border-squire-jeans bg-squire-jeans/10 text-squire-jeans" : "border-black/15 hover:border-black"}`}>
            {l} <span className="opacity-60">({counts[k]})</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-[#4B5563]">Stitching status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orderseg-status" className="border border-black/20 rounded px-3 py-1.5 text-sm outline-none">
            <option value="">All statuses</option>
            {(data?.statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>

      {loading ? <div className="text-sm text-[#4B5563] py-6">Loading…</div> : !data || data.items.length === 0 ? (
        <div className="border border-dashed border-black/15 rounded-lg p-10 text-center text-[#4B5563]">No orders in this view.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left sq-eyebrow"><th>ID</th><th>User</th><th>Total</th><th>Status</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {data.items.map((o) => (
              <tr key={o.id} className={`border-t border-black/10 ${o._missed ? "bg-red-50" : ""}`}>
                <td>{o.id}</td>
                <td>{o.user_id}</td>
                <td>{formatINR(o.total)}</td>
                <td><StatusBadge status={o._status} />{o._missed && <span className="ml-2 text-[10px] uppercase tracking-wider text-red-600">Missed</span>}</td>
                <td>{o.created_at?.slice(0, 10)}</td>
                <td><button data-testid={`orderseg-view-${o.id}`} onClick={() => setDetailId(o.id)} className="inline-flex items-center gap-1 text-xs hover:text-squire-jeans"><Eye size={15} /> View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && data.pages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="orderseg-pagination">
          <div className="text-xs text-[#4B5563]">{data.total} orders · page {data.page} of {data.pages}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page === 1} className="px-3 py-1.5 border border-black/20 rounded text-sm disabled:opacity-40">Prev</button>
            <button onClick={() => setPage((p) => Math.min(data.pages, p + 1))} disabled={data.page === data.pages} className="px-3 py-1.5 border border-black/20 rounded text-sm disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      <OrderDetailModal orderId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function FaqAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ question: "", answer: "" });
  const load = () => api.get("/faqs").then(({ data }) => setItems(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.question.trim()) { toast.error("Enter a question"); return; }
    try { await api.post("/faqs", { ...form, sort_order: items.length }); toast.success("FAQ added"); setForm({ question: "", answer: "" }); load(); }
    catch (e) { toast.error("Save failed"); }
  };
  const save = async () => { await api.put(`/faqs/${editingId}`, editForm); toast.success("Updated"); setEditingId(null); load(); };
  const del = async (id) => { if (!window.confirm("Delete this FAQ?")) return; await api.delete(`/faqs/${id}`); toast("Deleted"); load(); };

  return (
    <div className="mt-6 max-w-3xl">
      <div className="sq-eyebrow mb-1">FAQ</div>
      <p className="text-sm text-[#4B5563] mb-5">Questions shown on the home page. On hover the answer reveals.</p>

      <div className="space-y-3 mb-6">
        {items.map((f) => (
          <div key={f.id} className="border border-black/10 rounded-lg p-4" data-testid={`faq-row-${f.id}`}>
            {editingId === f.id ? (
              <div className="space-y-2">
                <input value={editForm.question} onChange={(e) => setEditForm({ ...editForm, question: e.target.value })} className="w-full border-b border-black/30 outline-none py-1 text-sm font-medium" />
                <textarea value={editForm.answer} onChange={(e) => setEditForm({ ...editForm, answer: e.target.value })} rows={2} className="w-full border border-black/15 rounded p-2 text-sm" />
                <div className="flex gap-3"><button onClick={save} className="sq-btn-primary !py-1.5">Save</button><button onClick={() => setEditingId(null)} className="text-xs text-[#4B5563]">Cancel</button></div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium">{f.question}</div>
                  <div className="text-sm text-[#4B5563] mt-1">{f.answer}</div>
                </div>
                <div className="flex gap-3 shrink-0">
                  <button onClick={() => { setEditingId(f.id); setEditForm({ question: f.question, answer: f.answer }); }} className="text-xs text-squire-jeans hover:underline">Edit</button>
                  <button onClick={() => del(f.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[#4B5563]">No FAQs yet.</div>}
      </div>

      <div className="border border-black/15 rounded-lg p-4 space-y-3">
        <div className="sq-eyebrow text-[10px]">New FAQ</div>
        <input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="Question" className="w-full border-b border-black/30 outline-none py-1 text-sm" data-testid="faq-new-question" />
        <textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="Answer" rows={2} className="w-full border border-black/15 rounded p-2 text-sm" data-testid="faq-new-answer" />
        <button onClick={create} data-testid="faq-add" className="sq-btn-primary">Add FAQ</button>
      </div>
    </div>
  );
}

function LocationsAdmin() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ state: "", city: "" });
  const load = () => api.get("/locations").then(({ data }) => setRows(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.state.trim() || !form.city.trim()) { toast.error("Enter state and city"); return; }
    try { await api.post("/locations", form); toast.success("Added"); setForm({ state: form.state, city: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/locations/${id}`); toast("Removed"); load(); };

  // group by state
  const byState = rows.reduce((acc, r) => { (acc[r.state] = acc[r.state] || []).push(r); return acc; }, {});

  return (
    <div className="mt-6 max-w-3xl">
      <div className="sq-eyebrow mb-1">States & Cities</div>
      <p className="text-sm text-[#4B5563] mb-5">These feed the State/City dropdowns on the checkout and bespoke address forms, and power the "most-ordered cities" statistics. Add each city under its state.</p>

      <div className="flex flex-wrap items-end gap-3 border border-black/15 rounded-lg p-4 mb-6">
        <label className="block"><span className="sq-eyebrow text-[10px]">State</span>
          <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Karnataka" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" /></label>
        <label className="block"><span className="sq-eyebrow text-[10px]">City</span>
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Belagavi" className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" /></label>
        <button onClick={add} data-testid="location-add" className="sq-btn-primary">Add City</button>
      </div>

      {Object.keys(byState).sort().map((st) => (
        <div key={st} className="mb-5">
          <div className="font-display text-lg mb-2">{st}</div>
          <div className="flex flex-wrap gap-2">
            {byState[st].sort((a, b) => a.city.localeCompare(b.city)).map((r) => (
              <span key={r.id} className="inline-flex items-center gap-2 border border-black/15 rounded-full pl-3 pr-1.5 py-1 text-sm" data-testid={`location-${r.id}`}>
                {r.city}
                <button onClick={() => del(r.id)} className="w-5 h-5 grid place-items-center rounded-full hover:bg-red-50 text-red-500">×</button>
              </span>
            ))}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="text-sm text-[#4B5563]">No locations yet — add your first city above.</div>}
    </div>
  );
}

function LookbookTilesAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ image: "", label: "", link: "", kind: "category", sort_order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => api.get("/lookbook-tiles").then(({ data }) => setItems(data || [])).catch(() => {});
  useEffect(() => { load(); api.get("/product-categories", { params: { top_level: true } }).then(({ data }) => setCats(data || [])).catch(() => {}); }, []);

  const linkOptions = [
    ...cats.map((c) => ({ label: c.label, value: `/shop/${c.key}` })),
    { label: "Accessories", value: "/accessories" },
    { label: "Fabrics", value: "/fabrics" },
    { label: "Bespoke", value: "/bespoke" },
  ];

  const create = async () => {
    if (!form.image) { toast.error("Add an image"); return; }
    try { await api.post("/lookbook-tiles", { ...form, sort_order: Number(form.sort_order) || 0 }); toast.success("Tile added"); setForm({ image: "", label: "", link: "", kind: "category", sort_order: 0 }); load(); }
    catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this tile?")) return; await api.delete(`/lookbook-tiles/${id}`); toast("Deleted"); load(); };
  const startEdit = (t) => { setEditingId(t.id); setEditForm({ image: t.image, label: t.label, link: t.link, kind: t.kind || "category", sort_order: t.sort_order || 0 }); };
  const saveEdit = async () => { await api.put(`/lookbook-tiles/${editingId}`, { ...editForm, sort_order: Number(editForm.sort_order) || 0 }); toast.success("Updated"); setEditingId(null); load(); };

  const TileForm = ({ val, setVal, onSubmit, submitLabel, testid }) => (
    <div className="border border-black/15 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={testid}>
      <ImageUploader value={val.image} onChange={(img) => setVal({ ...val, image: img })} multiple={false} label="Tile Image" testid={`${testid}-image`} />
      <div className="space-y-3">
        <label className="block"><span className="sq-eyebrow text-[10px]">Label (e.g. Shirts)</span>
          <input value={val.label} onChange={(e) => setVal({ ...val, label: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" /></label>
        <label className="block"><span className="sq-eyebrow text-[10px]">Type</span>
          <select value={val.kind} onChange={(e) => setVal({ ...val, kind: e.target.value })} className="w-full border border-black/20 rounded px-2 py-1.5 text-sm">
            <option value="category">Category (large, left/right columns)</option>
            <option value="accessory">Accessory (small, middle column)</option>
          </select></label>
        <label className="block"><span className="sq-eyebrow text-[10px]">Links to</span>
          <select value={linkOptions.some((o) => o.value === val.link) ? val.link : "__custom"} onChange={(e) => setVal({ ...val, link: e.target.value === "__custom" ? "" : e.target.value })} className="w-full border border-black/20 rounded px-2 py-1.5 text-sm">
            {linkOptions.map((o) => <option key={o.value} value={o.value}>{o.label} ({o.value})</option>)}
            <option value="__custom">Custom path…</option>
          </select>
          <input value={val.link} onChange={(e) => setVal({ ...val, link: e.target.value })} placeholder="/shop/shirts" className="w-full bg-transparent border-b border-black/20 outline-none py-1 text-xs mt-1" /></label>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs"><span>Order</span><input type="number" value={val.sort_order} onChange={(e) => setVal({ ...val, sort_order: e.target.value })} className="w-16 border-b border-black/20 outline-none py-1 text-sm" /></label>
          <button onClick={onSubmit} data-testid={`${testid}-submit`} className="sq-btn-primary ml-auto">{submitLabel}</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mb-12">
      <div className="sq-eyebrow mb-1">Trending Lookbook Tiles</div>
      <p className="text-sm text-[#4B5563] mb-5 max-w-3xl">Build the home "Trending Lookbook" grid. <strong>Category</strong> tiles are the large images in the left & right columns (Shirt, Pant, Blazer, Suit, Kurta…); <strong>Accessory</strong> tiles stack in the middle column. Each tile links to a page — tapping "Shirts" takes the customer to the shirts listing, etc.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {items.map((t) => (
          <div key={t.id} className="border border-black/10 rounded-lg overflow-hidden" data-testid={`lookbook-tile-row-${t.id}`}>
            <div className="aspect-[4/3] bg-[#F5F5F5]">{t.image && <img src={t.image} alt={t.label} className="w-full h-full object-cover" />}</div>
            <div className="p-2">
              <div className="text-sm font-medium truncate">{t.label || "—"}</div>
              <div className="text-[11px] text-[#9CA3AF]">{t.kind} · {t.link || "no link"}</div>
              <div className="flex gap-3 mt-1">
                <button onClick={() => startEdit(t)} className="text-xs text-squire-jeans hover:underline">Edit</button>
                <button onClick={() => del(t.id)} className="text-xs text-red-600 hover:underline">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-sm text-[#4B5563]">No tiles yet — add some below.</div>}
      </div>

      {editingId ? (
        <div className="space-y-3">
          <div className="sq-eyebrow text-[10px]">Editing tile</div>
          <TileForm val={editForm} setVal={setEditForm} onSubmit={saveEdit} submitLabel="Save Changes" testid="lookbook-tile-edit" />
          <button onClick={() => setEditingId(null)} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
        </div>
      ) : (
        <TileForm val={form} setVal={setForm} onSubmit={create} submitLabel="Add Tile" testid="lookbook-tile-new" />
      )}
    </div>
  );
}

function LookbookAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", season: "", cover_image: "", description: "", product_ids: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", season: "", cover_image: "", description: "", product_ids: "" });
  const load = () => api.get("/lookbook").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/lookbook", { ...form, product_ids: form.product_ids.split(",").map((s) => s.trim()).filter(Boolean) }); toast("Look saved"); setForm({ title: "", season: "", cover_image: "", description: "", product_ids: "" }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this look?")) return;
    try { await api.delete(`/lookbook/${id}`); toast("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const startEdit = (lk) => { setEditingId(lk.id); setEditForm({ title: lk.title, season: lk.season, cover_image: lk.cover_image, description: lk.description, product_ids: (lk.product_ids || []).join(", ") }); };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/lookbook/${id}`, { ...editForm, product_ids: editForm.product_ids.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Look updated");
      cancelEdit();
      load();
    } catch { toast.error("Update failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-2">
        {items.map((lk) => (
          editingId === lk.id ? (
            <div key={lk.id} className="border border-black/10 p-4" data-testid={`look-edit-${lk.id}`}>
              {[["title", "Title"], ["season", "Season"], ["description", "Description"], ["product_ids", "Product IDs (comma)"]].map(([k, l]) => (
                <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
                  <input value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                </label>
              ))}
              <div className="mt-3">
                <ImageUploader value={editForm.cover_image} onChange={(u) => setEditForm({ ...editForm, cover_image: u })} label="Cover Image" />
              </div>
              <div className="flex gap-3 mt-4">
                <button data-testid={`look-save-${lk.id}`} onClick={() => saveEdit(lk.id)} className="sq-btn-primary flex-1">Save Changes</button>
                <button onClick={cancelEdit} className="text-xs text-[#4B5563] hover:text-black">Cancel</button>
              </div>
            </div>
          ) : (
            <div key={lk.id} className="border border-black/10 p-4 flex gap-3" data-testid={`look-row-${lk.id}`}>
              <img src={lk.cover_image} alt="" className="w-20 h-24 object-cover" />
              <div className="flex-1">
                <div className="font-display text-lg">{lk.title}</div>
                <div className="sq-eyebrow">{lk.season} · {lk.product_ids?.length || 0} products</div>
                <div className="text-xs text-[#4B5563] mt-1">{lk.description}</div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <button data-testid={`look-edit-${lk.id}`} onClick={() => startEdit(lk)} className="text-xs hover:underline">Edit</button>
                <button data-testid={`look-delete-${lk.id}`} onClick={() => del(lk.id)} className="text-xs text-[#4B5563] hover:text-black">Delete</button>
              </div>
            </div>
          )
        ))}
      </div>
      <div className="border border-black/10 p-5 h-fit">
        <div className="sq-eyebrow">New Look</div>
        {[["title", "Title"], ["season", "Season"], ["description", "Description"], ["product_ids", "Product IDs (comma)"]].map(([k, l]) => (
          <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
            <input data-testid={`look-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
        ))}
        <div className="mt-3">
          <ImageUploader value={form.cover_image} onChange={(u) => setForm({ ...form, cover_image: u })} label="Cover Image" testid="look-cover" />
        </div>
        <button data-testid="look-create" onClick={create} className="sq-btn-primary w-full mt-5">Publish</button>
      </div>
    </div>
  );
}

function CRMAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ audience: "all", channel: "email", subject: "", message: "" });
  const load = () => api.get("/crm/broadcasts").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    try { const { data } = await api.post("/crm/broadcast", form); toast.success(`Broadcast ${data.id} → ${data.recipient_count} recipients (mock)`); load(); setForm({ ...form, subject: "", message: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-2">
        {items.length === 0 && <div className="text-sm text-[#4B5563]">No broadcasts yet.</div>}
        {items.map((b) => (
          <div key={b.id} className="border border-black/10 p-4" data-testid={`crm-row-${b.id}`}>
            <div className="flex justify-between"><div className="font-display">{b.subject}</div><div className="sq-eyebrow">{b.channel.toUpperCase()} · {b.audience}</div></div>
            <div className="text-xs text-[#4B5563] mt-1">{b.message?.slice(0, 120)}{b.message?.length > 120 ? "…" : ""}</div>
            <div className="sq-eyebrow text-[10px] mt-2">{b.id} · {b.recipient_count} recipients · {b.sent_at?.slice(0, 16).replace("T", " ")}</div>
          </div>
        ))}
      </div>
      <div className="border border-black/10 p-5 h-fit">
        <div className="sq-eyebrow">New Broadcast</div>
        <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Audience</span>
          <select data-testid="crm-audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            {["all", "users", "bespoke_buyers", "corporate"].map((a) => <option key={a}>{a}</option>)}
          </select>
        </label>
        <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Channel</span>
          <select data-testid="crm-channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            {["email", "sms", "whatsapp", "push"].map((a) => <option key={a}>{a}</option>)}
          </select>
        </label>
        <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Subject</span>
          <input data-testid="crm-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </label>
        <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Message</span>
          <textarea data-testid="crm-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border border-black/10 outline-none focus:border-squire-jeans p-2 text-sm resize-none" />
        </label>
        <button data-testid="crm-send" onClick={send} className="sq-btn-primary w-full mt-5">Send Broadcast</button>
        <p className="text-[10px] text-[#4B5563] mt-2">MOCKED — no real emails / SMS sent. Recipients counted from DB.</p>
      </div>
    </div>
  );
}

function AuditAdmin() {
  const [items, setItems] = useState([]);
  const [filterAction, setFilterAction] = useState("");
  const [filterActor, setFilterActor] = useState("");
  const load = async () => {
    const q = new URLSearchParams();
    if (filterAction) q.set("action", filterAction);
    if (filterActor) q.set("actor_id", filterActor);
    const { data } = await api.get(`/admin/audit?${q.toString()}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [filterAction, filterActor]);
  return (
    <div>
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div><div className="sq-eyebrow flex items-center gap-1"><ScrollText size={12} /> Filter action</div>
          <input data-testid="audit-filter-action" value={filterAction} onChange={(e) => setFilterAction(e.target.value)} placeholder="e.g. report.run" className="bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </div>
        <div><div className="sq-eyebrow">Actor</div>
          <input data-testid="audit-filter-actor" value={filterActor} onChange={(e) => setFilterActor(e.target.value)} placeholder="USER00001" className="bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </div>
        <div className="ml-auto sq-eyebrow text-[#4B5563]">{items.length} events</div>
      </div>
      <div className="space-y-1 max-h-[600px] overflow-y-auto sq-scroll">
        {items.length === 0 && <div className="text-sm text-[#4B5563]">No audit events yet.</div>}
        {items.map((a) => (
          <div key={a.id} className="grid grid-cols-12 gap-3 text-xs border-b border-black/5 py-2" data-testid={`audit-${a.id}`}>
            <div className="col-span-2 font-mono">{a.id}</div>
            <div className="col-span-2 sq-eyebrow text-[10px]">{a.created_at?.slice(0, 19).replace("T", " ")}</div>
            <div className="col-span-2">{a.actor_id} <span className="text-[#4B5563]">({a.actor_role})</span></div>
            <div className="col-span-3 font-semibold text-squire-jeans">{a.action}</div>
            <div className="col-span-3 text-[#4B5563]">{a.target} {a.target_id}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsAdmin() {
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState({ name: "Weekly Atelier Snapshot", frequency: "weekly", recipients: "" });
  const load = async () => {
    const [s, r] = await Promise.all([api.get("/admin/reports"), api.get("/admin/reports/runs")]);
    setItems(s.data); setRuns(r.data);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/admin/reports", { ...form, recipients: form.recipients.split(",").map((s) => s.trim()).filter(Boolean) });
      toast("Report schedule saved"); load(); setForm({ ...form, recipients: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const run = async (rid) => {
    try { const { data } = await api.post(`/admin/reports/${rid}/run`); toast.success(`Sent to ${data.recipients_count} recipients (mock)`); load(); }
    catch { toast.error("Failed"); }
  };
  const preview = async (rid) => {
    const t = localStorage.getItem("sq_token");
    const res = await fetch(`${api.defaults.baseURL}/admin/reports/${rid}/preview`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${rid}_preview.pdf`; a.click();
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-3">
        <div className="sq-eyebrow flex items-center gap-1"><FileText size={12} /> Schedules · {items.length}</div>
        {items.map((s) => (
          <div key={s.id} className="border border-black/10 p-4" data-testid={`report-${s.id}`}>
            <div className="flex flex-wrap justify-between gap-2">
              <div>
                <div className="font-display text-lg">{s.name}</div>
                <div className="sq-eyebrow mt-1">{s.id} · {s.frequency} · {s.recipients?.length} recipient(s) · last run {s.last_run_at ? s.last_run_at.slice(0, 16).replace("T", " ") : "never"}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`report-preview-${s.id}`} onClick={() => preview(s.id)} className="text-xs border border-black/30 px-3 py-1 hover:border-squire-jeans">Preview PDF</button>
                <button data-testid={`report-run-${s.id}`} onClick={() => run(s.id)} className="text-xs bg-squire-jeans text-white px-3 py-1 inline-flex items-center gap-1"><Play size={12} /> Run & Send</button>
              </div>
            </div>
          </div>
        ))}
        <div className="sq-eyebrow mt-6 mb-2">Recent runs · {runs.length}</div>
        <div className="space-y-1 max-h-[280px] overflow-y-auto sq-scroll">
          {runs.length === 0 && <div className="text-sm text-[#4B5563]">No runs yet.</div>}
          {runs.map((r) => (
            <div key={r.id} className="text-xs border-b border-black/5 py-2 flex justify-between" data-testid={`report-run-row-${r.id}`}>
              <span className="font-mono">{r.id}</span>
              <span className="sq-eyebrow">{r.ran_at?.slice(0, 16).replace("T", " ")}</span>
              <span>₹{r.snapshot?.revenue_orders?.toFixed(0)} orders · ₹{r.snapshot?.revenue_bespoke?.toFixed(0)} bespoke</span>
              <span className="text-squire-jeans">{r.delivery_status}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="border border-black/10 p-5 h-fit">
        <div className="sq-eyebrow">New Schedule</div>
        {[["name", "Name"], ["recipients", "Recipients (comma emails)"]].map(([k, l]) => (
          <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
            <input data-testid={`report-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
        ))}
        <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Frequency</span>
          <select data-testid="report-frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            {["daily", "weekly", "monthly"].map((f) => <option key={f}>{f}</option>)}
          </select>
        </label>
        <button data-testid="report-create" onClick={create} className="sq-btn-primary w-full mt-5">Save Schedule</button>
        <p className="text-[10px] text-[#4B5563] mt-2">Stats reports are MOCKED — PDF generated & recipients logged; no real email sent (Phase 4 = Resend / SendGrid integration).</p>
      </div>
    </div>
  );
}


function LifecycleAdmin() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const search = async () => {
    if (!q.trim()) return;
    try { const { data } = await api.get(`/admin/lifecycle?q=${encodeURIComponent(q)}`); setData(data); }
    catch { toast.error("Failed"); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-6">
        <input data-testid="lifecycle-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user (name/email/phone/USER##) or order ID" className="flex-1 bg-transparent border-b border-black/30 outline-none py-2" />
        <button data-testid="lifecycle-search" onClick={search} className="sq-btn-primary">Search</button>
      </div>
      {data?.matches?.length === 0 && <div className="text-sm text-[#4B5563]">No matches.</div>}
      {data?.matches?.map((m) => (
        <div key={m.user.id} className="border border-black/10 p-6 mb-6" data-testid={`lifecycle-${m.user.id}`}>
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <div className="font-display text-2xl">{m.user.name}</div>
              <div className="sq-eyebrow text-[10px]">{m.user.id} · {m.user.email} · {m.user.phone}</div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div><div className="font-display text-2xl">{m.totals.orders}</div><div className="sq-eyebrow text-[10px]">Orders</div></div>
              <div><div className="font-display text-2xl">{m.totals.bespoke}</div><div className="sq-eyebrow text-[10px]">Bespoke</div></div>
              <div><div className="font-display text-2xl text-squire-jeans">{formatINR(m.totals.lifetime_value)}</div><div className="sq-eyebrow text-[10px]">LTV</div></div>
              <div><div className="font-display text-2xl">{m.totals.returns}</div><div className="sq-eyebrow text-[10px]">Returns</div></div>
            </div>
          </div>
          <div className="mt-6 relative pl-8">
            <div className="absolute left-3 top-0 bottom-0 w-px bg-black/10" />
            {m.events.map((e, i) => (
              <div key={i} className="relative pb-4">
                <div className="absolute -left-5 top-1 w-3 h-3 rounded-full bg-squire-jeans" />
                <div className="text-xs text-[#4B5563]">{e.at?.slice(0, 16).replace("T", " ")} · <span className="font-semibold text-squire-jeans">{e.kind}</span></div>
                <div className="text-sm font-display">{e.title}</div>
                {e.subject && <div className="text-xs text-[#4B5563] mt-1">{e.subject}</div>}
              </div>
            ))}
            {!m.events?.length && <div className="text-sm text-[#4B5563]">No events.</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function CorporateAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/corporate/leads").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => { try { await api.patch(`/admin/corporate/${id}`, patch); toast("Updated"); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="space-y-3">
      {items.length === 0 && <div className="text-sm text-[#4B5563]">No corporate leads yet.</div>}
      {items.map((q) => (
        <div key={q.id} className="border border-black/10 p-4" data-testid={`corp-lead-${q.id}`}>
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <div className="font-display text-lg">{q.company} <span className="sq-eyebrow text-[10px]">— {q.contact_name}</span></div>
              <div className="sq-eyebrow text-[10px] mt-1">{q.id} · {q.product_type} × {q.quantity} · {formatINR(q.total)} · GST {q.gst || "—"}</div>
              <div className="text-xs text-[#4B5563] mt-1">{q.email} · {q.phone} · {q.notes}</div>
            </div>
            <div className="flex gap-2 items-center">
              <select data-testid={`corp-status-${q.id}`} value={q.status} onChange={(e) => update(q.id, { status: e.target.value })} className="text-xs bg-transparent border border-black/20 px-2 py-1">
                {["new", "in_review", "quoted", "negotiating", "won", "lost"].map((s) => <option key={s}>{s}</option>)}
              </select>
              <input data-testid={`corp-mgr-${q.id}`} defaultValue={q.account_manager_id || ""} placeholder="Manager USER##" onBlur={(e) => update(q.id, { account_manager_id: e.target.value })} className="text-xs bg-transparent border-b border-black/30 outline-none w-32" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NotificationsAdmin() {
  const [form, setForm] = useState({ user_id: "", title: "", body: "", kind: "system", link: "" });
  const send = async () => {
    try { const { data } = await api.post("/admin/notifications", { ...form, user_id: form.user_id || null }); toast.success(data.broadcast ? `Broadcast to ${data.recipients}` : `Sent to ${data.user_id}`); setForm({ ...form, title: "", body: "" }); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="max-w-xl border border-black/10 p-6">
      <div className="sq-eyebrow">Send notification</div>
      {[["user_id", "User ID (leave empty to broadcast)"], ["title", "Title"], ["link", "Link (optional)"]].map(([k, l]) => (
        <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
          <input data-testid={`notif-admin-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
        </label>
      ))}
      <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Body</span>
        <textarea data-testid="notif-admin-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full bg-transparent border border-black/10 outline-none focus:border-squire-jeans p-2 text-sm" />
      </label>
      <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Kind</span>
        <select data-testid="notif-admin-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
          {["order", "return", "referral", "offer", "system"].map((k) => <option key={k}>{k}</option>)}
        </select>
      </label>
      <button data-testid="notif-admin-send" onClick={send} className="sq-btn-primary mt-5 w-full">Send</button>
    </div>
  );
}

function RecoAdmin() {
  const [pins, setPins] = useState([]);
  const [form, setForm] = useState({ context: "product", product_id: "", target_product_id: "", note: "" });
  const load = () => api.get("/admin/recommendations/pins").then(({ data }) => setPins(data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/recommendations/pin", form); toast("Pinned"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { await api.delete(`/admin/recommendations/pin/${id}`); load(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-2">
        {pins.length === 0 && <div className="text-sm text-[#4B5563]">No pinned recommendations yet.</div>}
        {pins.map((p) => (
          <div key={p.id} className="border border-black/10 p-3 flex justify-between" data-testid={`pin-${p.id}`}>
            <div>
              <div className="font-display">{p.product_id} → {p.context}{p.target_product_id ? ` · target ${p.target_product_id}` : ""}</div>
              <div className="sq-eyebrow text-[10px]">{p.id} · {p.note}</div>
            </div>
            <button data-testid={`pin-del-${p.id}`} onClick={() => del(p.id)} className="text-xs text-[#4B5563] hover:text-squire-jeans">Unpin</button>
          </div>
        ))}
      </div>
      <div className="border border-black/10 p-5 h-fit">
        <div className="sq-eyebrow">Pin recommendation</div>
        <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Context</span>
          <select data-testid="pin-context" value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
            {["product", "cart", "wardrobe", "home"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        {[["product_id", "Product ID to pin"], ["target_product_id", "Target Product ID (when context=product)"], ["note", "Note"]].map(([k, l]) => (
          <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
            <input data-testid={`pin-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </label>
        ))}
        <button data-testid="pin-create" onClick={create} className="sq-btn-primary w-full mt-5">Pin</button>
      </div>
    </div>
  );
}

